/**
 * 自由创作正文 —— UX-1(2026-05-30)从 FreeCreatePage 抬起,塞进统一创建 shell。
 *
 * 与原 FreeCreatePage 的差异:
 *   - 顶部 segment(文字/图改图)上移到 shell;本组件由 `mode` prop 驱动 form.mode
 *     (文字↔图改图 切换时本组件不卸载,prompt 等状态保留)。
 *   - 标题/返回链接上移到 shell;保存草稿按钮留在本组件顶部工具条。
 *   - footer 改用共享 `PosterSubmitFooter`。
 *
 * 业务 hook(useFreeCreateForm/Constraints/Actions)与 section 组件零改写。
 */
import { SKU, imageVariant } from "@/entities/pricing/model/priceBook";
import { useQuotePreview } from "@/entities/pricing/model/useQuote";
import { useEffect, useMemo, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useMutation } from "@tanstack/react-query";
import { Image as ImageIcon, Loader2, Save, Sparkles, Type } from "lucide-react";

import { Button } from "@/shared/ui/button";
import { cn } from "@/shared/lib/utils";
import { toast } from "@/shared/hooks/use-toast";
import { posterService } from "../../../api/posterService";
import { errorText, errorTitle, errorToast } from "@/shared/lib/apiErrors";
import { posterErrorToast } from "@/shared/lib/poster-errors";
import { useConfirm } from "@/shared/hooks/use-confirm";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/shared/ui/dialog";
import { POLISH_COST_LABEL } from "@/entities/scene-card/model/creditRates";
import { normalizeRoles } from "../../../model/free-create/groupScript";
import { RAW_PROMPT_MAX, buildRawPrompt } from "../../../model/free-create/buildRawPrompt";
import { GroupScriptPanel, MAX_GROUP_POINTS } from "./GroupScriptPanel";
import { useBrandAssets } from "@/entities/brand-asset";
import { PosterSourceBar } from "../PosterSourceBar";
import {
  resolvePosterCreateSource,
  shouldOfferPickPurpose,
  shouldRenderSourceBar,
  type PosterSourceBarData,
} from "../../../model/posterSource";
import { useFreeCreateForm } from "../../../model/free-create/useFreeCreateForm";
import type { Ratio } from "../../../model/free-create/useFreeCreateForm";
import { measureImageFile, nearestRatio } from "../../../model/free-create/nearestRatio";
import {
  detectRatioDetailedFromInputs,
  resolveRatioRuling,
} from "../../../model/free-create/detectRatio";
import { useFreeCreateConstraints } from "../../../model/free-create/useFreeCreateConstraints";
import { useFreeCreateActions } from "../../../model/free-create/useFreeCreateActions";
import { useMaskClearOnProviderSwitch } from "../../../model/free-create/useMaskClearOnProviderSwitch";
import {
  CAPABILITIES_DOWN_REASON,
  canSubmitFreeCreate,
  freeCreateDisabledReason,
} from "../../../model/free-create/submitGate";

import { BrandPanel } from "../../../ui/components/free-create/BrandPanel";
import { ParamsPopover } from "../../../ui/components/free-create/ParamsPopover";
import { PromptPanel } from "../../../ui/components/free-create/PromptPanel";
import { RefImageUpload } from "../../../ui/components/free-create/RefImageUpload";
import { MultiRefImagesCard } from "../../../ui/components/free-create/MultiRefImagesCard";
import {
  only1KReason as only1KReasonFor,
  refImagesBlockedReason,
} from "../../../model/free-create/multiRefImages";
import { RemixRecipePicker } from "../../../ui/components/free-create/RemixRecipePicker";
import { MaskPainter } from "../../../ui/components/free-create/MaskPainter";
import type { MaskPainterHandle } from "../../../ui/components/free-create/MaskPainter";
import {
  hasUnfilledSlot,
  skeletonFor,
  type RemixRecipe,
} from "../../../model/free-create/remixRecipes";
import { StylePanel } from "../../../ui/components/free-create/StylePanel";
import { FinalPromptFold } from "../../../ui/components/free-create/FinalPromptFold";
import { PosterSubmitFooter } from "../../../ui/components/create/PosterSubmitFooter";
import { CreateMirrorColumn } from "../../../ui/components/create/CreateMirrorColumn";
import { isOpenAiFamilyProvider, posterProviderLabel } from "../../../model/providerLabels";
import {
  findStyleByTone,
  styleLabelForTone,
  styleSampleUrl,
} from "../../../ui/components/free-create/styleOptions";
import { FONT_STYLE_AUTO, FONT_STYLES } from "../../../ui/components/free-create/fontStyleOptions";
import {
  HANDOFF_DEFAULT_RATIOS,
  parseRatioSetHandoff,
  ratioSetPrompt,
} from "../../../model/free-create/ratioSetHandoff";
// ★`ProductPickerDrawer` 2026-08-01 上午随产品入口迁出本文件,下午又被商品主图那条路引回来
//   —— 但**位置变了**:它不再是文生图大框里的一个 chip,而是「商品主图」这条路进页的第一步。
//   判据从「你在哪个页面」换成了「你带着哪个用途进来」(`isGoodsMain`)。
import { ProductPickerDrawer } from "./ProductPickerDrawer";
import { PromptEntries } from "./PromptEntries";
import { detectOverload, POINTS_PER_IMAGE } from "../../../model/free-create/overloadHint";
import {
  detectMetaInstruction,
  stripMetaInstruction,
} from "../../../model/free-create/metaInstructionHint";
import { PosterModeHandoff } from "./PosterModeBar";
import { GenerateConfirmDialog, type ConfirmTrigger } from "./GenerateConfirmDialog";
import { isCoverAt } from "../../../model/free-create/groupScript";
import { consistencyKind } from "../../../model/free-create/consistencyNote";
import { deriveGroupPoints, parsePosterExplosiveState } from "../../../model/blueprint";
import { buildReferenceContent } from "@/entities/explosive";
import {
  DEFAULT_RATIO_SET,
  detectOutputMode,
  effectiveCount as effectiveCountOf,
  submittableGroupPoints,
  shouldInferRatio,
} from "../../../model/free-create/outputMode";
import {
  clearSceneCardState,
  parseScenePreset,
  sceneKeyOf,
} from "@/entities/scene-card/model/presets";

interface Props {
  /** 文字 → T2I,图改图 → I2I */
  mode: "T2I" | "I2I";
  /** 段控切换(回写 URL ?tab,由页面驱动) */
  onModeChange: (mode: "T2I" | "I2I") => void;
  /**
   * 2026-08-01 —— 把消费完的用途报给外壳(页头认用途不认模式)。
   * ★由本组件报,而不是让外壳再解析一遍 `location.state`:消费时会清 history state,
   *   两个读者抢同一份一次性 state 是靠 effect 先后决定输赢的写法。
   */
  onSceneKeyChange?: (sceneKey: string | null) => void;
  /**
   * 2026-08-01 —— 从 URL `?scene=` 恢复用途(刷新 / 分享链接 / 新标签页这一态)。
   * ★只恢复**身份**:预填(groupPoints / ratios)仍只走一次性的 `scenePreset`,
   *   不因为刷新而重放 —— 那正是 `clearSceneCardState` 当初要防的事。
   */
  initialSceneKey?: string | null;
}

export function FreeCreateBody({ mode, onModeChange, onSceneKeyChange, initialSceneKey }: Props) {
  const { t } = useTranslation("poster");
  const brandQ = useBrandAssets();
  const brand = brandQ.data;

  const form = useFreeCreateForm();
  const isI2I = form.mode === "I2I";

  /**
   * 2026-08-01 —— 商品主图这条路。
   * ★判据就是 sceneKey 本身,**不给 SceneCard / ScenePreset 加字段**:
   *   `posterPurpose.ts` 文件头记着上次给 `types.ts` 加字段会与视频线撞在同一批行上。
   */
  const isGoodsMain = form.sceneKey === "poster_single";
  /**
   * 从 URL 恢复用途(刷新 / 分享链接)。一次性:之后以 `form.sceneKey` 为准,
   * 否则用户点「退出场景」把它清空后,URL 里那个残留会立刻把身份又塞回来。
   */
  const sceneRestoredRef = useRef(false);
  useEffect(() => {
    if (sceneRestoredRef.current) return;
    sceneRestoredRef.current = true;
    if (initialSceneKey && !form.sceneKey) {
      form.setSceneKey(initialSceneKey);
      // ★组图/套图的「模式」是身份的必然推论,不是预填(0810 E2E VIS-GRP-03):
      //   只恢复 sceneKey 不开模式,提交时 groupPoints/ratioSet 为空 → 后端落
      //   requested_count=1、type_payload 不写 GROUP —— 一单顶着组图 scene_key 的
      //   **假组图**进库,详情页按单图渲染,打包下载/轮播全不在场。
      //   空模板(手填爆点/默认三比例)不含任何用户内容,与「不重放预填」不冲突。
      if (initialSceneKey === "poster_group") {
        form.setGroupPoints([]);
        form.setRatioSet(null);
      } else if (initialSceneKey === "poster_multiratio") {
        form.setRatioSet([...DEFAULT_RATIO_SET]);
        form.setGroupPoints(null);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  // 用途一变就报给外壳(退出场景 `clearSceneOrigin` 置 null 时也要同步回默认页头 + 清 URL)。
  useEffect(() => {
    onSceneKeyChange?.(form.sceneKey);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [form.sceneKey]);
  /**
   * 锚用哪张实拍(2026-08-03 拍板:默认第 1 张主图,给「换一张」不做自动挑图 ——
   * 挑错了用户没处说理)。换产品归零。
   */
  const [sampleIdx, setSampleIdx] = useState(0);
  useEffect(() => {
    setSampleIdx(0);
  }, [form.product?.code]);
  const sampleImages = form.product?.sampleImages ?? [];
  /** 选中的产品有没有实拍图(有才谈得上「用它当底/当锚」)。用途无关,三条产品路共用。 */
  const productSampleKey = sampleImages[sampleIdx] ?? sampleImages[0] ?? null;
  /**
   * 实拍自动接成 i2i 底的两条路:商品主图(2026-08-01 批次2)+ 全渠道套图(2026-08-03
   * 设计稿 A3 —— 套图的产品锚就是「拿实拍当底出三比例」,与详情页「出全渠道套图」同构)。
   * 组图不走这条:它的锚走 groupAnchorRef → backend anchorImageUrl(B2-1 外部锚通路)。
   */
  const productAnchorKey =
    isGoodsMain || form.sceneKey === "poster_multiratio" ? productSampleKey : null;

  /* 折叠段的一行摘要 —— 收起来也要能一眼看到当前设置,否则为了确认得挨个点开 */
  const styleSummary = [
    form.selectedStyle?.trim() || "自由发挥",
    form.styleTokens ? "已定配色" : "AI 配色",
    FONT_STYLES.find((f) => f.key === form.fontStyle)?.label || "自动字体",
  ].join(" · ");
  const brandSummary = (() => {
    const logo = brand?.logos?.find((l) => l.id === form.logoId);
    const qr = brand?.qrs?.find((q) => q.id === form.qrId);
    const parts = [
      logo ? `LOGO ${logo.name}` : "不叠 LOGO",
      qr ? `二维码 ${qr.name}` : "不叠二维码",
    ];
    if (form.useBrandColors) parts.push("用品牌色");
    // 折叠态摘要必须说得出「这次会不会印电话」—— 会改变成品的开关不能只活在展开区里
    if (form.printContact && (brand?.phone || brand?.email)) parts.push("印联系方式");
    return parts.join(" · ");
  })();

  // 顶层 tab(文字/图改图)→ 同步 form.mode(本组件不卸载,状态保留)。
  useEffect(() => {
    if (form.mode !== mode) form.setMode(mode);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mode]);

  // 详情页「基于此再创作」回填:location.state.prefillPrompt → 文案框,prefillNote → 特别要求(各填一次)。
  const location = useLocation();
  const prefilledRef = useRef(false);
  useEffect(() => {
    const st = location.state as { prefillPrompt?: string; prefillNote?: string } | null;
    if (st?.prefillPrompt && !prefilledRef.current && !form.prompt) {
      prefilledRef.current = true;
      form.setPrompt(st.prefillPrompt);
      if (st.prefillNote && !form.extraConstraint) form.setExtraConstraint(st.prefillNote);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.state]);

  // P-B 蓝图预载(从爆款情报路由来):一次性 ref 消费 + 逐字段验型(脏 state 整包丢弃);
  // 预载后进入组图模式(爆点只认拆解报告的 explosivePoints;取不到就留空等手填 —— 走查 W2),
  // 套图关(互斥)。
  const explosivePrefilledRef = useRef(false);
  useEffect(() => {
    if (explosivePrefilledRef.current) return;
    const handoff = parsePosterExplosiveState(location.state);
    if (!handoff) return;
    explosivePrefilledRef.current = true;
    form.setExplosive(handoff);
    form.setGroupPoints(deriveGroupPoints(handoff));
    form.setRatioSet(null);
    if (!form.prompt) form.setPrompt(`按「${handoff.formulaName}」出组图:`);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.state]);

  // B2 场景卡 preset(创意设计中心点卡进来):唯一验型器一次性消费,消费后清 history
  // state(刷新不重放)。explosive 蓝图预载优先,两者不叠加(上面 effect 同一轮已置 ref)。
  const scenePresetRef = useRef(false);
  useEffect(() => {
    if (scenePresetRef.current || explosivePrefilledRef.current) return;
    const preset = parseScenePreset(location.state);
    if (!preset) return;
    scenePresetRef.current = true;
    clearSceneCardState();
    form.setSceneKey(sceneKeyOf(preset)); // B6 打标(随提交上送;直接进页不打标)
    if (preset.kind === "poster_group") {
      // 组图模式开(空模板 = 用户手填爆点),与套图互斥
      form.setGroupPoints(preset.groupPoints);
      form.setRatioSet(null);
    } else if (preset.kind === "poster_multiratio") {
      form.setRatioSet(preset.ratios as Ratio[]);
      form.setGroupPoints(null);
    }
    // ★ poster_single 这里仍不预填 count/ratio/mode:造假预填会替用户选了卡面没承诺的东西。
    //
    // ★★2026-08-01 更正上一版结论。原话是「poster_single 不预填是**对的**,它本来就是
    //   创建页默认态」—— 那句话在**用途只当埋点**的前提下成立,而那个前提已经没了:
    //   `sceneKey` 现在会经 `sceneUsageOf` 变成归整请求里的 `context.purpose`
    //   (见 `useFreeCreateActions.runNormalize`),商品主图与文生图不再是同一张脸。
    //   「商品主图 ≡ 文生图」正是这条注释记录的旧状态,别再拿它当依据。
    //   商品主图的产品化(进页选商品 + 产品图当锚)在批次 2,见
    //   `docs/superpowers/plans/2026-08-01-visual-cabin-product-main-image.md`。
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.state]);

  /**
   * T4(2026-07-29)—— 从详情页「出全渠道套图」进来:把那张成品原图接成 i2i 底图。
   *
   * ★为什么**不是**在浏览器里「下载再上传」(2026-07-30 改掉了,7.29 反馈任务 21):
   *   原来的理由是「两个桶越权判据不同,走一次真上传就零后端改动」。代价是让浏览器去
   *   `fetch` 那张 4K 原图 —— 而它**必然失败**:页面 CSP 是 `connect-src 'self'`,请求在
   *   发出去之前就被拒(实测同一个 URL `<img>` 能加载,`fetch` 连 `mode:'no-cors'` 都抛 →
   *   排除「只是缺 ACAO 头」)。用户看到的那句原生 `Failed to fetch` 就是这么来的。
   *   现在改成后端按 assetId 在对象存储内部复制(`/poster/assets/{id}/adopt-as-ref`),
   *   越权校验与其余 `/assets/{id}/*` 端点同口径 —— 跨域、超时、4K 图占浏览器内存一并消失。
   * ★取的是 baseUrl(无 overlay 原图):成品图上已经合成了 AIGC 角标与 LOGO/二维码,
   *   拿它当底图会被当画面内容重绘(角标糊掉、二维码扫不出)。
   */
  const ratioSetHandoffRef = useRef(false);
  useEffect(() => {
    if (ratioSetHandoffRef.current) return;
    const handoff = parseRatioSetHandoff(location.state);
    if (!handoff) return;
    ratioSetHandoffRef.current = true;

    onModeChange("I2I");
    form.setRatioSet(HANDOFF_DEFAULT_RATIOS);
    // ★点「出全渠道套图」本身就是一次显式的画幅选择 —— 不置这个标记的话
    //   `ratioFollowsSource`(i2i 且没手选过比例)恒为 true,⚙ 参数里的「出什么」整块不渲染:
    //   ratioSet 设了、会真出 3 张扣 3 份钱,而用户在界面上看不到也改不了。
    form.setRatioUserTouched(true);
    form.setGroupPoints(null); // 与组图互斥(模型层也锁了)
    form.setPrompt(ratioSetPrompt(handoff.prompt));

    void (async () => {
      try {
        if (handoff.assetId != null) {
          // ★主路(2026-07-30,任务 21):后端在对象存储内部把这张产物复制成参考图。
          //   浏览器不碰图片字节 —— 老路是 `fetch(presignedUrl)` 下 4K 原图再上传,而它
          //   **必然失败**:页面 CSP 是 `connect-src 'self'`,请求根本发不出去(实测同一个
          //   URL `<img>` 能加载、`fetch` 连 no-cors 都抛,所以不是缺 CORS 头)。
          //   用户看到的那句原生 `Failed to fetch` 就是这么来的。
          const res = await posterService.adoptAssetAsRef(handoff.assetId);
          adoptRefResult(res);
          return;
        }
        // 兜底:老的 history state 没带 assetId(用户开着页面时我们发了版)。这条路会被 CSP
        // 拦住,所以失败时要把真因和出路说清楚,不要只甩一句 Failed to fetch。
        const resp = await fetch(handoff.baseUrl);
        if (!resp.ok) throw new Error(`下载原图失败 ${resp.status}`);
        const blob = await resp.blob();
        const file = new File([blob], `poster-${handoff.taskId}-${handoff.assetSeq}.png`, {
          type: blob.type || "image/png",
        });
        uploadRefMut.mutate(file);
      } catch (e) {
        // ★失败要说清楚:静默退回 T2I 的话,用户按「出全渠道套图」得到的是三张**重画**的图,
        //   而他要的是同一张换画幅 —— 那种失败比报错更糟。
        const raw = e instanceof Error ? e.message : "";
        const isBlockedByBrowser = /Failed to fetch|NetworkError|Load failed/i.test(raw);
        toast({
          title: "没能把这张图接过来",
          description: isBlockedByBrowser
            ? "这个页面是从旧版本打开的,浏览器不让它去取原图。刷新一下再点「出全渠道套图」即可;" +
              "急用的话也可以在详情页下载原图,回到这里手动上传。"
            : errorText(e, { hiddenDescription: "请回详情页重试,或手动下载后上传" }),
          variant: "destructive",
        });
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.state]);

  /** 退出场景:清打标 + 把该场景设过的东西复位回默认态(不动用户后来自己改的)。 */
  const clearSceneOrigin = () => {
    form.setSceneKey(null);
    form.setGroupPoints(null);
    form.setRatioSet(null);
  };

  const groupModeActive = form.groupPoints !== null;
  /**
   * 产品块的判据 = **用途需要产品资料**(2026-08-01 拍板延伸,2026-08-03 设计稿方案 A)。
   * 三条产品用途路:商品主图 / 爆点组图 / 全渠道套图。组图看 groupModeActive 而不看
   * sceneKey —— 追爆舱 handoff 进来的组图没有 sceneKey,但它同样是产品用途。
   * 纯文生图 / 图改图保持干净(用户原话「文生图图生图需要简单一些」)。
   */
  const isRatioUsage = form.sceneKey === "poster_multiratio";
  const productUsage = isGoodsMain || groupModeActive || isRatioUsage;
  const productBlockTitle = isGoodsMain
    ? "先选一个商品"
    : groupModeActive
      ? "这组图卖哪个商品?"
      : "这套图投放哪个商品?";
  const navigate = useNavigate();

  /**
   * 来源条:**判定**在 `model/posterSource.ts`(纯函数 + 真值表锁),这里只把判定结果
   * 接上本页的回调 —— 改之前是两个各自独立的 if,两条 bar 可能同时渲染。
   */
  const source = resolvePosterCreateSource({
    isI2I,
    groupModeActive,
    sceneKey: form.sceneKey,
    productPicked: form.product !== null,
  });
  const sourceBarData: PosterSourceBarData =
    source.kind === "explosive"
      ? {
          kind: "explosive",
          formulaName: form.explosive?.formulaName ?? null,
          sourcePlatform: form.explosive?.sourcePlatform,
          payload: form.explosive?.payload,
          referenceContent: buildReferenceContent(form.explosive?.breakdown),
          // BPWB-1(2026-09-03):蓝图工作卡的播放位判据 —— 随 handoff 原样带过来
          blueprintId: form.explosive?.blueprintId,
          hasRawVideo: form.explosive?.hasRawVideo,
          // 原样嵌蓝图工作卡(BlueprintHeroCard):爆点仍可增删改
          editable: {
            onSwap: () => {
              window.location.assign("/ai-explosive");
            },
            onClear: () => {
              form.setExplosive(null);
              form.setGroupPoints(null);
            },
          },
        }
      : source.kind === "product"
        ? {
            kind: "product",
            productName: form.product?.name ?? "",
            // 「换产品」= 回到选择器那一行(它就在下面,不跳页、不清文案)
            onChange: () => {
              document
                .querySelector<HTMLSelectElement>('select[aria-label="从产品库选产品"]')
                ?.focus();
            },
          }
        : source.kind === "scene"
          ? {
              kind: "scene",
              sceneKey: form.sceneKey,
              ...(source.canExitScene ? { onClear: clearSceneOrigin } : {}),
            }
          : {
              kind: "free",
              /**
               * 「挑个目的」必然跳去舱内页(本页没有页内目的选择器),而创建页的草稿是
               * **手动保存**的 —— 跳走就丢。视频侧 codex 第五轮抓到的正是这一类。
               *
               * ★为什么**不做「脏没脏」的判断**,一律先确认:
               *   前两版试过枚举「哪些字段算有内容」,codex 连着两轮指出漏了字段
               *   (先是「特别要求」,再是画风/字体档/品牌 logo/二维码/画幅/张数/引擎/清晰度)——
               *   这类判断**永远证明不了自己列全了**,而漏一个的代价是静默吃掉用户的选择。
               *   一律确认就没有这一类 bug 了:多一次点击,换掉一整类数据丢失。
               *
               * ★为什么页头那个「返回」不确认、这里要确认(不是前后不一致):
               *   「返回」本身就读作"我要离开这一页",用户按下去时**预期**会离开;
               *   而「挑个目的」读起来像页内助手,没人预期它会把已填的东西冲掉 ——
               *   要挡的是这个**预期落差**,不是「离开」这个动作本身。
               *   (页面级的"所有出口都拦"是另一件事,不在本波范围。)
               *
               * ★2026-07-31 —— **图改图不给这个入口**。
               *   `resolvePosterCreateSource` 在 i2i 且没选产品/场景卡时同样返回 `free`,
               *   于是「挑个目的」跟着长在了改图那条路上。两件事都不对:
               *   ① **代价不对称**:点它要离开创建页,而改图态下用户已经传了参考图、
               *      可能还涂了 mask、写了改图指令 —— 一走全没;
               *   ② **去处也不对**:卡墙上那些"目的"卡(商品主图 / 爆点组图 / 多比例套图…)
               *      承诺的全是**从零创作**的 T2I 形态,挑完回来仍然不是「改这张图」。
               *      给一个点进去解决不了他问题的入口,比不给更糟。
               *   判断放在**构造处**而不是渲染处:「这个动作存不存在」只决定一次,
               *   `PosterSourceBar` 与页内动作条都按 `onPickPurpose` 在不在来渲染,
               *   两边自动一致,不会"改一处漏一处"。
               */
              ...(shouldOfferPickPurpose(isI2I)
                ? {
                    onPickPurpose: () => {
                      // 文案对「页面是空的」也成立 —— 不断言「你的内容会丢失」(空页面时那是假话),
                      // 只说清「不会保留」,并给出想保留时该怎么做。
                      if (
                        window.confirm(
                          "离开创建页去挑目的?\n\n本页没保存的内容不会保留。想留着就先取消,用右上角「保存草稿」。"
                        )
                      ) {
                        navigate("/ai-haibao");
                      }
                    },
                  }
                : {}),
            };

  const {
    capabilities,
    enabledProviders,
    supportedRatios,
    supportedTiers,
    validRatio,
    validTier,
    validCount,
    constraints,
    capabilitiesError,
  } = useFreeCreateConstraints({
    provider: form.provider,
    setProvider: form.setProvider,
    ratio: form.ratio,
    resolutionTier: form.resolutionTier,
    count: form.count,
    useGptThinking: form.useGptThinking,
  });

  // W2-B9 多图参考:组图 / 套图态不带(原因显示在卡上);引擎能力读 supportsMultiRef。
  const refImagesBlocked = refImagesBlockedReason({
    groupMode: form.groupPoints !== null,
    ratioSetMode: !!form.ratioSet && form.ratioSet.length >= 2,
  });
  const refImagesActive = form.refImages.length > 0 && !refImagesBlocked;
  const multiRefSupported = capabilities?.[form.provider]?.supportsMultiRef === true;
  /** 局部重绘只有万相实现(B5);OPENAI 家族不出画布。 */
  const maskSupported = !isOpenAiFamilyProvider(form.provider);
  /**
   * 多图参考卡。★1020:图改图时它排在「要改的图」正下方、改叫「再加参考图」——
   * 原来它在页面最底下也叫「参考图」,和顶上那张(其实是被改的底图)同名,用户问「怎么上下两个参考图」。
   */
  const multiRefCard = (
    <MultiRefImagesCard
      images={form.refImages}
      onChange={form.setRefImages}
      disabled={form.submitting}
      blockedReason={refImagesBlocked}
      engineUnsupported={!multiRefSupported}
      base={isI2I && form.refImage ? (productAnchorKey ? "product" : "upload") : "none"}
    />
  );
  // 挂了多图参考才锁 1K(改图本身不锁,见 only1KReason 头注)。
  // 落到 1K 的同时 ⚙ 里 2K/4K 不再展示,报价跟着走 1K 单价。
  const only1KReason = only1KReasonFor({ hasRefImages: refImagesActive });
  useEffect(() => {
    if (only1KReason && form.resolutionTier !== "1K") form.setResolutionTier("1K");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [only1KReason, form.resolutionTier]);

  // ★提到 useFreeCreateActions 之前:出图前的红线闸要把它当回调传进去(原来定义在 180 行以后)
  const confirmDialog = useConfirm();
  const actions = useFreeCreateActions({
    form,
    brand,
    validRatio,
    validTier,
    validCount,
    multiRefSupported,
    // 出图前红线闸(2026-08-14):蓝图参考句现在是可见的**值**,但一字未改就出图 =
    // 把别人家的宣传语印进自家物料(0813 真出过)。弹窗归 UI,判据在 outputMode。
    confirmUntouchedReference: (titles) =>
      confirmDialog({
        title: `有 ${titles.length} 张的大字还是参考片原话`,
        description:
          "这几句是参考片说的,不是你的产品文案 —— 直接出图会把它印在成品上:\n" +
          titles.map((t) => `· ${t}`).join("\n") +
          "\n建议返回改成自己的说法;确认要照原样出图也可以。",
        confirmText: "照原样出图",
        cancelText: "返回修改",
        variant: "warning",
      }),
  });

  // ───── i2i 参考图上传 ─────
  // 2026-07-13 —「跟随原图」真实现:选图即量宽高就近映射 5 档回填 form.ratio(修「图改图
  // 恒出 1:1」:此前该文案只是摆设,提交仍是默认 1:1)。用户手选(ratioUserTouched)不覆盖;
  // 量图失败保持现值,提交侧另带 ratioFollowsSource 让 ai 端按参考图实测兜底。
  const [sourceRatio, setSourceRatio] = useState<Ratio | null>(null);
  /**
   * R5 mask 上传。★每次落笔结束都会重传一版 —— mask 小(黑白 PNG,几十 KB)且必须与
   * 提交时的画面一致;存"最后一次涂完的"比在提交时才传要稳(提交那一刻再传会把失败
   * 变成"点了出图却报上传错误")。
   */
  // ★「只认最后一次」:mask 每落一笔就重传一版,慢的那次可能后回来,把新的覆盖成旧的
  //   (屏幕上画着 B、提交带的是 A);清空同理 —— 迟到的成功会把刚清掉的 mask 装回去。
  //   语义与判据在 model/free-create/latestOnly.ts(时序缺陷,内联 ref 没法测)。
  // 涂没涂放在 form 上(**单一真相**):保存草稿的提示要读它 —— 各存一份的话,
  // 「涂选不会一起保存」这句永远不出现(codex 十一轮 P3)。
  const painterRef = useRef<MaskPainterHandle>(null);

  // B5(0925 收尾波):局部重绘只有万相实现,切成 OPENAI 家族时若已经涂过 mask,
  // 真的清空画布(不止清 maskPainted 布尔值)并 toast 说明 —— 判据/理由见 hook 头注。
  useMaskClearOnProviderSwitch({
    provider: form.provider,
    maskPainted: form.maskPainted,
    setMaskPainted: form.setMaskPainted,
    painterRef,
  });

  const uploadRefMut = useMutation({
    mutationFn: (file: File) => posterService.uploadRefImage(file),
    onSuccess: (res, file) => {
      // 换了底图:画笔会按新图重建离屏 mask(onImgLoad),这里只把界面状态归位
      form.setMaskPainted(false);
      form.setRefImage({
        objectKey: res.objectKey,
        tempUrl: res.tempUrl,
        previewUrl: URL.createObjectURL(file),
      });
      void measureImageFile(file).then((dims) => {
        const measured = dims ? nearestRatio(dims.width, dims.height) : null;
        setSourceRatio(measured);
        if (measured && !form.ratioUserTouched) form.setRatio(measured);
      });
    },
    onError: (e: unknown) => toast(posterErrorToast(e)),
  });
  /**
   * 服务端复制成参考图之后的界面归位(2026-07-30,任务 21)。
   *
   * 与 uploadRefMut.onSuccess 的差别只在**没有本地 File**:图片字节从头到尾没进过浏览器,
   * 所以预览直接用服务端签的 tempUrl,实测画幅改成加载那张 URL 量(measureImageFile 吃 File,
   * 这里没有 File 可喂)。★量不出来就**不**动比例 —— 交接过来的默认三画幅是用户点「出全渠道
   * 套图」的意图本身,拿一个失败的测量去覆盖它比不测更糟。
   */
  const adoptRefResult = (res: { objectKey: string; tempUrl: string }) => {
    form.setMaskPainted(false);
    form.setRefImage({
      objectKey: res.objectKey,
      tempUrl: res.tempUrl,
      previewUrl: res.tempUrl,
    });
    const im = new Image();
    im.onload = () => {
      const measured = nearestRatio(im.naturalWidth, im.naturalHeight);
      setSourceRatio(measured);
      if (measured && !form.ratioUserTouched) form.setRatio(measured);
    };
    im.src = res.tempUrl;
  };

  /**
   * 商品主图:选中有实拍图的商品 → 把那张图接成 i2i 底图(默认开)。
   *
   * ★走后端存储内复制(`adoptProductImageAsRef`),**不在前端 fetch 存储域** ——
   *   页面 CSP 是 `connect-src 'self'`,那条路在请求发出去之前就被拒。
   * ★复用 `adoptRefResult`:与「出全渠道套图」接图走同一段落地逻辑(置底图 + 清画笔 +
   *   量原图比例)。另写一份迟早漂 —— 漏掉量比例那步的表现是「画幅没跟着原图走」。
   * ★失败不静默:退回从零画的话,用户拿到的是一张重画的图,而他要的是「就是这个杯子」。
   */
  const anchoredKeyRef = useRef<string | null>(null);
  useEffect(() => {
    // productAnchorKey 已把用途闸(商品主图 | 全渠道套图)算了进去 —— 套图 A3:同一段接图逻辑
    if (!form.product || !productAnchorKey) return;
    if (anchoredKeyRef.current === productAnchorKey) return;
    anchoredKeyRef.current = productAnchorKey;
    const code = form.product.code;
    void posterService
      .adoptProductImageAsRef(code, sampleIdx)
      .then((res) => {
        adoptRefResult(res);
        onModeChange("I2I");
      })
      .catch((e) => {
        anchoredKeyRef.current = null;
        toast(
          errorToast(e, {
            fallbackTitle: "没能把这个商品的实拍图接过来",
            hiddenDescription: "这次会从零画,画面里的商品可能与实物有出入。可以重选一次商品重试。",
          })
        );
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [form.product?.code, productAnchorKey]);

  /**
   * 组图产品锚(2026-08-03 设计稿 A2,默认开可关):adopt 拿 objectKey 存 form.groupAnchorRef,
   * **不**切 I2I、不占底图位 —— 组图仍是 T2I fan-out 的形态,锚经 backend 改键 anchorImageUrl
   * 走 B2-1 外部锚通路(逐张 GROUP 前缀 i2i:锁版面、放取景)。
   * 失败不静默:锚接不上要说,这次不锚照出,产品可能与实物有出入。
   */
  const groupAnchorTarget =
    groupModeActive && form.groupAnchorOn && form.product ? productSampleKey : null;
  /** 锚图预览(adopt 返回的 tempUrl)—— 「换一张」不能盲换,锚的是哪张要看得见。 */
  const [groupAnchorPreview, setGroupAnchorPreview] = useState<string | null>(null);
  const groupAnchorAdoptedRef = useRef<string | null>(null);
  useEffect(() => {
    if (!groupAnchorTarget || !form.product) {
      groupAnchorAdoptedRef.current = null;
      setGroupAnchorPreview(null);
      if (form.groupAnchorRef) form.setGroupAnchorRef(null);
      return;
    }
    if (groupAnchorAdoptedRef.current === groupAnchorTarget) return;
    groupAnchorAdoptedRef.current = groupAnchorTarget;
    void posterService
      .adoptProductImageAsRef(form.product.code, sampleIdx)
      .then((res) => {
        form.setGroupAnchorRef(res.objectKey);
        setGroupAnchorPreview(res.tempUrl || null);
      })
      .catch((e) => {
        groupAnchorAdoptedRef.current = null;
        setGroupAnchorPreview(null);
        form.setGroupAnchorRef(null);
        toast(
          errorToast(e, {
            fallbackTitle: "没能把实拍图接过来当锚",
            hiddenDescription:
              "这次整组从零画,画面里的商品可能与实物有出入。可以重选一次商品重试。",
          })
        );
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [groupAnchorTarget, form.product?.code, sampleIdx]);

  /**
   * AI 排台本(2026-08-03 设计稿 A):一次轻 LLM 把「产品/用户文案」排成逐张台本,
   * 全部落进台本卡逐字可改 —— AI 是旁路按钮,台本仍是唯一真相。
   * ★默认补排不覆盖(用户拍板):已有手写卡时只补到 6 张;想全重排先删再点。
   * ★排台本本身免费(poster.text_assist=FREE);fallback(LLM 挂 → 卖点逐字)提示里不提积分,
   *   说「这一次不收」会暗示平时收费(0926 修复波)。
   */
  const [drafting, setDrafting] = useState(false);
  /** coverage:AI 排的每条大字 → 它改写自输入的哪句(title 键,重排不丢)。 */
  const [draftSources, setDraftSources] = useState<Record<string, string>>({});
  const [styleOpen, setStyleOpen] = useState(false);
  const canDraft = !!form.product || form.prompt.trim().length >= 20;

  const handleDraftScript = async () => {
    const existing = form.groupPoints ?? [];
    const titled = existing.filter((pt) => pt.title.trim());
    if (existing.length >= MAX_GROUP_POINTS) {
      toast({ title: "台本已满 6 张", description: "删几张再让 AI 补排。" });
      return;
    }
    if (titled.length > 0) {
      const ok = await confirmDialog({
        title: `保留已写的 ${titled.length} 张,AI 只补新的?`,
        description: "不动你已写的大字和画面,只把台本补排到 6 张。想全部重排:先删掉现有的再点。",
        confirmText: "保留并补排",
      });
      if (!ok) return;
    }
    setDrafting(true);
    try {
      const res = await posterService.draftGroupScript({
        product: form.product
          ? {
              name: form.product.name,
              sellingPoints: form.product.sellingPoints,
              // 规格拍平成「label value」字符串:ai 侧只当语料(数字有源判据的输入)
              specs: form.product.specs.map((sp) => `${sp.label} ${sp.value}`.trim()),
              summary: form.product.summary || undefined,
            }
          : undefined,
        userText: form.prompt.trim() || undefined,
        maxPoints: MAX_GROUP_POINTS,
        // ★截 40:自定义画风存的是整段描述,整段发过去被 ai schema(≤40)422 —— 线上 18:07
        //   连续五发就是它;画风给排台本只是协调语境,前 40 字足够
        styleHint: form.selectedStyle ? form.selectedStyle.slice(0, 40) : undefined,
      });
      const have = new Set(existing.map((pt) => pt.title.trim()).filter(Boolean));
      const merged = [...existing];
      const sources: Record<string, string> = { ...draftSources };
      for (const pt of res.points) {
        const title = pt.title.trim().slice(0, 30);
        if (!title || have.has(title) || merged.length >= MAX_GROUP_POINTS) continue;
        merged.push({ title, ...(pt.detail ? { detail: pt.detail.slice(0, 120) } : {}) });
        have.add(title);
        if (pt.source) sources[title] = pt.source;
      }
      form.setGroupPoints(normalizeRoles(merged));
      setDraftSources(sources);
      if (res.subjectSpec) form.setGroupSubjectSpec(res.subjectSpec.slice(0, 60));
      // tone 只在整组说明还空着时代填 —— 用户写过的字一个都不动
      if (res.tone && !form.prompt.trim()) form.setPrompt(res.tone);
      toast(
        res.fallback
          ? {
              title: "AI 这次没排出来,按卖点逐字填了",
              // 排台本免费(text_assist=FREE):这里不提收费,旧文案「这一次不收」会暗示平时要收。
              description: "可以稍后再点一次,或直接手改。",
            }
          : {
              title: `台本排好了 · ${res.points.length} 张`,
              description: "每张都能改;大字旁的 ⓘ 能看到它改写自哪句。",
            }
      );
    } catch (e: unknown) {
      toast(posterErrorToast(e));
    } finally {
      setDrafting(false);
    }
  };

  const clearRefImage = () => {
    form.setRefImage(null);
    form.setMaskPainted(false); // 底图没了,画笔也不再渲染
    setSourceRatio(null);
    // 未手选时复位,避免残留的实测比例悄悄影响下一次 T2I/换图
    if (!form.ratioUserTouched) form.setRatio("1:1");
  };

  // V11.5「AI 润色进行中也禁生成按钮」等基础条件已并进 submitGate,这里不再留副本。
  // B1:闸认**有字卡**数 —— 三张全空卡不算「有台本」。
  const groupPointsOk =
    !groupModeActive || (submittableGroupPoints(form.groupPoints)?.length ?? 0) >= 1;
  /**
   * R3 codex P2:能力卡打完底后指令里还留着「〈新背景长什么样〉」这种待填段时,
   * 长度判据是过的 —— 于是占位符会被原样发给引擎。与长图「还没填画面描述」同口径:
   * 提交闸认这个字面量,填完才放行。
   */
  const remixSlotUnfilled = isI2I && hasUnfilledSlot(form.prompt);
  // 闸门本体抽在 model/free-create/submitGate.ts(纯函数,判据钉在那边)。
  // ★这里**没有** mask 相关的闸:mask 在提交那一刻才导出+上传(见 resolveMask),
  //   整段都在提交自己的忙碌态里,没有"边传边点"的窗口需要挡。
  // 0926:能力表挂了 → 档位/渠道未知、报不出价。按钮禁用 + 原因贴按钮 + 底栏「价格待系统核算」,
  //   toast 只弹一次、走公共错误层(标题按码),描述用固定人话 —— 不把后端原文(曾含内部地址)透给用户。
  //   全局 QueryCache 兜底 toast 已在 useProviderCapabilities 退订,不会双报。
  const capabilitiesDown = capabilitiesError != null;
  useEffect(() => {
    if (!capabilitiesError) return;
    toast({
      title: errorTitle(capabilitiesError, { fallbackTitle: "出图服务暂时不可用" }),
      description: CAPABILITIES_DOWN_REASON,
      variant: "destructive",
    });
  }, [capabilitiesError]);
  const gateInput = {
    serviceUnavailable: capabilitiesDown,
    isI2I,
    submitting: form.submitting,
    polishing: form.polishing,
    scheduleReady: form.scheduleMode === "now" || !!form.scheduledAt,
    refUploading: uploadRefMut.isPending,
    hasRefImage: !!form.refImage,
    prompt: form.prompt,
    remixSlotUnfilled,
    groupPointsOk,
  };
  const canSubmit = canSubmitFreeCreate(gateInput);
  // 0926 第八批 Z3:按钮变灰的每一种原因都贴在按钮旁(与闸门同源),不再只说能力表挂了这一种。
  const submitDisabledReason = freeCreateDisabledReason(gateInput);

  // 画幅裁决 —— 提交侧 useFreeCreateActions / 画幅框 mirrorRatio / 底栏 label / 弹卡
  // requestGenerate 四处共用这一份(resolveRatioRuling,口径见 detectRatio.ts 头注)。
  // ★此前底栏「手选先返回」而提交侧「文案先」—— 爱民手选 3:4、底栏回显 3:4,
  //   发出去的却是词面推的 1:1:承诺和单不是同一个数。
  const ratioDetection = detectRatioDetailedFromInputs(
    form.originalPromptSnapshot ?? form.prompt,
    form.extraConstraint
  );
  const ratioRuling = resolveRatioRuling({
    detection: ratioDetection,
    pinnedRatio: form.ratioUserTouched || isI2I ? validRatio : null,
  });

  // ⚙ 参数 Popover — 两个模式共用;i2i 时比例语义「跟随原图」。
  const paramsSlot = (
    <ParamsPopover
      supportedRatios={supportedRatios}
      validRatio={validRatio}
      /* 1005:组图摘要写**实际派发**的画幅(与底栏/提交侧同一份 ratioRuling)——
         生产实测按钮写「组图 1:1」、底栏和出图都是 3:4。 */
      groupRatio={ratioRuling.ratio ?? validRatio}
      setRatio={form.setRatio}
      ratioUserTouched={form.ratioUserTouched}
      setRatioUserTouched={form.setRatioUserTouched}
      validCount={validCount}
      supportedTiers={supportedTiers}
      validTier={validTier}
      setResolutionTier={form.setResolutionTier}
      isCombinationLegal={constraints.isCombinationLegal}
      resolveSize={constraints.resolveSize}
      provider={form.provider}
      setProvider={form.setProvider}
      enabledProviders={enabledProviders}
      useGptThinking={form.useGptThinking}
      setUseGptThinking={form.setUseGptThinking}
      /* T4:i2i 也能出套图 —— 但**只在挂了原图时**给这个控件。
         空手 i2i 不给两个点不动的按钮,这正是当初把它禁掉的理由,要留住。
         (ai 侧 ratios 只与 groupPoints 互斥、与 I2I 正交;挂了原图时每个比例
          都会以那张原图走 image_edit,正是「同一张换画幅」要的语义。) */
      ratioSet={isI2I && !form.refImage ? null : form.ratioSet}
      setRatioSet={isI2I && !form.refImage ? undefined : form.setRatioSet}
      /* 组图爆点只读透传:弹层据此判形态给指引,增删走「组图模式」条。
         i2i 传 null —— 图改图没有组图(比例跟随原图)。 */
      groupPoints={isI2I ? null : form.groupPoints}
      keepSubjectConsistent={form.keepSubjectConsistent}
      setKeepSubjectConsistent={isI2I ? undefined : form.setKeepSubjectConsistent}
      ratioFollowsSource={isI2I && !form.ratioUserTouched}
      sourceRatio={isI2I && form.refImage ? sourceRatio : null}
      only1KReason={only1KReason}
    />
  );

  // 本次生效画幅 — 常驻底栏显示(2026-07-17:连续创作时比例会退回默认,用户需一眼可见)。
  const effectiveRatioLabel = (() => {
    const promptText = form.originalPromptSnapshot ?? form.prompt;
    if (isI2I && !form.ratioUserTouched) {
      const follow = t("freeCreate.ratioFollowSource");
      return sourceRatio ? `${follow} · ${sourceRatio}` : follow;
    }
    if (ratioRuling.ratio) return ratioRuling.ratio;
    // ★2026-07-31:组图/套图态不会走智能推断(见 shouldInferRatio),这里就不能写「智能」——
    //   右列写着「组图 1:1」、底栏写「智能」、实际发第三个值,是同一件事的三处口径。
    return shouldInferRatio({
      mode: groupModeActive
        ? "GROUP"
        : form.ratioSet && form.ratioSet.length >= 2
          ? "RATIO_SET"
          : "MULTI",
      isI2I,
      textRatioFound: false,
      ratioUserTouched: form.ratioUserTouched,
      hasText: (promptText ?? "").trim().length > 0,
    })
      ? t("freeCreate.ratioSmart")
      : validRatio;
  })();

  /**
   * R3:指令来自哪张能力卡(仅卡面高亮用)。用户改了指令**不清除** —— 它记的是"来源",
   * 不是"状态";清掉会让卡面在用户微调一个字之后忽然全灭,像是白点了。
   */
  const [remixRecipeKey, setRemixRecipeKey] = useState<string | null>(null);
  /** vision 卡(照这张再做一版)正在读参考图 —— 卡面转圈 + 禁止重复点。 */
  const [describing, setDescribing] = useState(false);

  /**
   * 点卡时**必须同时清掉的 AI 优化态**(codex R5)。
   *
   * `handleGenerate` 在 `isAiOptimized` 为真时,rawPrompt 是用
   * `contentDescription/systemConstraints` 拼的,**根本不读 prompt** —— 用户先在文字 tab
   * 用过 AI 优化、再切到改图点卡,提交上去的会是上一条海报的优化文案,卡片指令一个字
   * 都到不了引擎(界面 A 成片 B)。
   *
   * ★字段表在 `remixRecipes.RECIPE_RESET_FIELDS`(单测锁死「漏清一个都算错」)。
   *   两条点卡路径(普通卡 / vision 卡)都走这里 —— 抽出来正是为了不出现"一条路复位了、
   *   另一条忘了"的半拉子。
   */
  const resetOptimizedState = () => {
    form.setIsAiOptimized(false);
    form.setContentDescription("");
    form.setSystemConstraints("");
    form.setOriginalPromptSnapshot(null);
  };

  /**
   * 任务 20「照这张再做一版」——**唯一**真的能出「类似但不同」的那条路。
   *
   * ★为什么必须切回文字模式(而不是加个"弱一点"的开关):
   *   i2i 这条路上,原图**永远是 edit base**。三张老卡的骨架再怎么改语气、
   *   万相再怎么调提示词,模型都是在那张图上改 —— 「做个类似的」出来一模一样
   *   是结构决定的,不是措辞不够狠。所以这里换的是结构:
   *   **参考图 → AI 看懂它 → 一段文字 → 文生图**,原图只贡献"长什么样"的信息。
   * ★描述落进指令框、并把 tab 切到「文字」,是**故意让它可见**:用户能看见我们
   *   从他的图里读出了什么,不同意就改。创作选择要可见,只有工程质量类的才黑盒。
   * ★失败**不静默**:读不到就报错、卡面复原。兜一段泛泛描述会让他拿到一张
   *   与参考图毫无关系的图,还以为系统看过了。
   */
  const applyVisionRecipe = async (r: RemixRecipe) => {
    const objectKey = form.refImage?.objectKey;
    if (!objectKey) {
      toast({ title: "先传一张参考图", description: "这张卡是照着你给的图再做一版。" });
      return;
    }
    setDescribing(true);
    try {
      const description = await posterService.describeReference(objectKey);
      setRemixRecipeKey(r.key);
      form.setPrompt(skeletonFor(r, description));
      resetOptimizedState();
      // 结构性的一步:切到文字模式 → 提交时不再带 coverImageRef = 原图不当 edit base。
      onModeChange("T2I");
      toast({
        title: "已按这张图写好画面描述",
        description: "接下来走的是文生图,不在原图上改 —— 描述可以随便改。",
      });
    } catch (e) {
      toast(posterErrorToast(e));
    } finally {
      setDescribing(false);
    }
  };

  const applyRecipe = (r: RemixRecipe) => {
    if (r.vision) {
      void applyVisionRecipe(r);
      return;
    }
    setRemixRecipeKey(r.key);
    form.setPrompt(skeletonFor(r));
    resetOptimizedState();
  };

  /**
   * T2「改成我自己写」:把附加段全文并进文案框,并**复位它的来源选择器**。
   *
   * ★复位是必须的,不是顺手做的:不复位的话 `buildRawPrompt` 提交时还会再拼一遍,
   *   同一段产品资料在指令里出现两次(而界面上看不出来)。这条与
   *   `applyRecipe` 清 AI 优化态是同一类 —— 两个出口写同一件事,必须一起改。
   */
  const inlineSegment = (segment: "product" | "style" | "font", text: string) => {
    const target = form.isAiOptimized ? form.contentDescription : form.prompt;
    const merged = target.trim() ? `${target.trimEnd()}\n${text}` : text;
    if (form.isAiOptimized) form.setContentDescription(merged);
    else form.setPrompt(merged);

    if (segment === "product") form.setProduct(null);
    if (segment === "style") {
      actions.handleStyleChange(null);
      form.setStyleTokens(null);
    }
    if (segment === "font") form.setFontStyle(FONT_STYLE_AUTO);
  };

  /**
   * 真实出图张数(与 estimatedCredits / 提交侧 requestedCount 同源)。
   *
   * ★2026-08-13 浏览器走查抓的:这里原来是**手写的一条 fallback 链**,
   * 组图态但一张都还没写字时 `length > 0` 不成立 → 它会一路退到 `validCount`(默认 1),
   * 于是底栏写着「1 个爆点各出一张 · 预计扣 280 积分」,而实际一张也出不了
   * (主按钮确实是灰的 —— 但那是另一个判据在管,两个出口说了两套话)。
   *
   * 蓝图交接改成「N 个空位」之后,**每一次从蓝图进组图都会撞上这个 0 张态**,
   * 原来靠预填有字才碰不到。改用 `outputMode` 里那个唯一实现:GROUP 态就报组图张数,
   * 是 0 就报 0。ParamsPopover 一直用的就是它 —— 现在两处终于同源。
   */
  const collectedPointsForCount = submittableGroupPoints(form.groupPoints);
  const effectiveCount = effectiveCountOf(detectOutputMode(form.groupPoints, form.ratioSet), {
    groupPoints: collectedPointsForCount,
    ratioSet: form.ratioSet,
    count: validCount,
  });

  /**
   * B1-4「这一组凭什么像一套」—— 输入是**提交时真会发出去的那几个值**:
   * 张数走 effectiveCount(与费用同源)、锚开关走 form.keepSubjectConsistent、
   * 挂没挂原图走 isI2I。★这里另算一份就会和实际发出去的漂移。
   */
  const consistency = consistencyKind({
    mode: groupModeActive
      ? "GROUP"
      : form.ratioSet && form.ratioSet.length >= 2
        ? "RATIO_SET"
        : "MULTI",
    isI2I,
    keepSubjectConsistent: form.keepSubjectConsistent,
    count: effectiveCount,
  });

  // 右回显列的派生值(全部取自已有 form/常量,不新增状态)
  const mirrorStyleThumb = (() => {
    const opt = findStyleByTone(form.selectedStyle);
    return opt?.key ? (styleSampleUrl(opt.key) ?? null) : null;
  })();
  const mirrorFontLabel =
    FONT_STYLES.find((f) => f.key === form.fontStyle)?.label ?? FONT_STYLES[0].label;
  const providerLabel = posterProviderLabel(form.provider);
  /**
   * 右列用的「这次生效的画幅」—— 画幅框与【画幅构图】段共用一个值。
   *
   * ★两处必须同源:框画成 3:4、指令里写 1:1 的法则,用户没法发现哪个是真的。
   * ★0831 起与提交侧共用 ratioRuling(pinned > 显式数字比例 > 语境化词面):
   *   此前这里手选一律赢、提交侧文案一律赢 —— 爱民那单框画的 3:4、发出去的 1:1。
   * ★推不出来时是 null(智能档 + 文案里没渠道词) → 画幅框走 3:4 占位、
   *   【画幅构图】段整段不出现。提交时 `handleGenerate` 还会再调一次
   *   `/poster/infer-ratio` 拿 LLM 的 5 选 1,那个结果这里拿不到 ——
   *   预览少一段总比预览编一段强(编出来的那段有一半概率与真出图的法则相反)。
   */
  const mirrorRatio = ratioRuling.ratio;
  /**
   * 【画幅构图】段用的画幅 —— i2i 恒 null。
   * ★i2i 的 rawPrompt 是**编辑指令**,往里缀版面法则会让模型重新构图(改不动原图);
   *   与提交侧 `useFreeCreateActions` 同一个判据,两处分叉就会出现「右边显示会带、
   *   实际不带」。画幅框仍按 mirrorRatio 画形状 —— 那是"这张图多大",不是"怎么构图"。
   */
  const compositionRatio = isI2I ? null : mirrorRatio;
  /**
   * 最终指令长度(2026-08-03 用户实拍:文案框写 1691/2000,提交却报 2153 超限)。
   * ★口径打架的根:框内计数只数文案,提交闸数的是 buildRawPrompt 拼完的整串
   * (风格/字体/画幅/产品附加都算)。这里用**同一个纯函数**算出真数,
   * 喂给文案框计数器 —— 显示口径与提交口径必须是同一个数。
   */
  const finalPromptLen = buildRawPrompt({
    isAiOptimized: form.isAiOptimized,
    prompt: form.prompt,
    contentDescription: form.contentDescription,
    systemConstraints: form.systemConstraints,
    selectedStyle: form.selectedStyle,
    styleTokens: form.styleTokens,
    fontStyle: form.fontStyle,
    product: form.product,
    showProductSpecs: form.showProductSpecs,
    absorbed: form.absorbed,
    ratio: compositionRatio,
  }).length;

  /**
   * 「这一张装不下」的建议 —— 爆点组图不是一个要预先选的形态,是**内容超载时的出口**。
   * 只在我们看得出装不下的那一刻出现;已经在组图态 / 改图态都不提(见 overloadHint 头注)。
   */
  const overload = detectOverload({
    product: form.product,
    text: form.prompt,
    groupModeActive,
    isI2I,
  });

  /**
   * 任务 19 —— 「这句像是排版要求,不是要印上去的字」。
   *
   * ★只在**文字这条路**查:i2i 那个框里写的本来就是**指令**,「用宋体」在那里是正常的
   *   改图要求,不是会被印出来的正文 —— 在那儿弹这一问就是纯打扰。
   * ★查的是**当前框里那段字**:已整理态绑的是 `contentDescription`(用户看见的那段),
   *   查 `prompt` 会指着一段他已经看不到的原文说话。
   * ★用户被劝掉一次就不再问(`metaHintDismissed`)—— 他说要印就是要印。
   */
  const metaHintText = form.isAiOptimized ? form.contentDescription : form.prompt;
  const [metaHintDismissed, setMetaHintDismissed] = useState<string | null>(null);
  const metaHintRaw = isI2I ? null : detectMetaInstruction(metaHintText);
  const metaHint = metaHintRaw && metaHintRaw.matched === metaHintDismissed ? null : metaHintRaw;

  /**
   * 品牌/特别要求入口条 —— PromptPanel(单图/套图)与组图台本卡**共用同一个节点**
   * (B1,2026-08-03):组图态 hero 大框退役后,这排入口随槽位搬进台本卡,
   * 两处再各写一份迟早漂成两套状态。
   */
  const promptEntriesNode = (
    <PromptEntries
      // 产品入口在别处(商品主图=页顶独立块 / 组图=台本卡① / 套图=页顶块),这里别再指路
      extraElsewhere={productUsage}
      brandSlot={
        <BrandPanel
          dense
          brand={brand}
          logoId={form.logoId}
          setLogoId={form.setLogoId}
          qrId={form.qrId}
          setQrId={form.setQrId}
          useBrandColors={form.useBrandColors}
          setUseBrandColors={form.setUseBrandColors}
          printContact={form.printContact}
          setPrintContact={form.setPrintContact}
          logoAnchor={form.logoAnchor}
          setLogoAnchor={form.setLogoAnchor}
          qrAnchor={form.qrAnchor}
          setQrAnchor={form.setQrAnchor}
        />
      }
      brandLabel={brandSummary}
      brandActive={!!form.logoId || !!form.qrId || form.useBrandColors || form.printContact}
      extraConstraint={form.extraConstraint}
      setExtraConstraint={form.setExtraConstraint}
      disabled={form.submitting}
    />
  );

  /** 「AI 排台本」按钮行(台本卡 ① 区)。 */
  const groupDraftSlot = (
    <div className="flex flex-wrap items-center gap-2">
      <Button
        type="button"
        size="sm"
        className="h-7 gap-1 px-2.5 text-[12.5px]"
        disabled={form.submitting || drafting || !canDraft}
        onClick={handleDraftScript}
      >
        {drafting ? (
          <Loader2 className="h-3.5 w-3.5 animate-spin" />
        ) : (
          <Sparkles className="h-3.5 w-3.5" />
        )}
        {drafting ? "正在排…" : "AI 排台本"}
        {!drafting && <span className="text-2xs font-normal opacity-80">{POLISH_COST_LABEL}</span>}
      </Button>
      <span className="text-2xs text-muted-foreground">
        {canDraft
          ? "按产品/文案排出每张的大字与画面,排完随便改"
          : "先选商品,或在上面写 ≥20 字文案,再让 AI 排"}
      </span>
    </div>
  );

  /** 「整组风格」chip(C:画风/配色/字体三条带在组图态收敛进台本卡,点开弹层选)。 */
  const groupStyleSlot = (
    <>
      <button
        type="button"
        onClick={() => setStyleOpen(true)}
        className="rounded-full border border-border px-2.5 py-0.5 text-[12px] text-foreground/80 transition-colors hover:border-primary/50 hover:text-foreground"
        data-testid="group-style-chip"
      >
        🎨 整组风格:{styleSummary} ▾
      </button>
      <Dialog open={styleOpen} onOpenChange={setStyleOpen}>
        <DialogContent className="max-h-[82vh] max-w-3xl overflow-y-auto">
          <DialogHeader>
            <DialogTitle>整组风格</DialogTitle>
          </DialogHeader>
          <p className="-mt-1 text-[12.5px] text-muted-foreground">
            组图的一致性要求全组一个风格 —— 这里选一次,整组每张都用它。
          </p>
          <StylePanel
            selectedStyle={form.selectedStyle}
            setSelectedStyle={actions.handleStyleChange}
            fontStyle={form.fontStyle}
            setFontStyle={form.setFontStyle}
            styleTokens={form.styleTokens}
            setStyleTokens={form.setStyleTokens}
            followStyle={form.followStyle}
            resetFollowStyle={form.resetFollowStyle}
          />
          {/* ★收口按钮(2026-08-03 用户实测「没有保存入口」):选择本身是即时生效的,
              但弹层里没有一个明确的"完成"动作,用户不知道选没选上、怎么退出 ——
              交互需要一个收口,哪怕它只是关门。按钮上回显当前选择,点了就是确认。 */}
          <div className="mt-3 flex items-center justify-end gap-3 border-t border-border/50 pt-3">
            <span className="text-[12.5px] text-muted-foreground">当前:{styleSummary}</span>
            <Button type="button" size="sm" onClick={() => setStyleOpen(false)}>
              就用这套,完成
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );

  /** 一致性摘要行(B:四层机制说成三句人话;点不动,只交代)。 */
  const groupConsistencySlot = (
    <div
      className="flex flex-wrap items-center gap-1.5 text-2xs"
      data-testid="group-consistency-row"
    >
      <span className="text-muted-foreground">一致性:</span>
      {form.groupAnchorOn && productSampleKey && form.product ? (
        <span className="rounded-full bg-emerald-500/10 px-2 py-0.5 font-medium text-emerald-600 dark:text-emerald-400">
          ✓ 产品:照实拍画
        </span>
      ) : form.groupSubjectSpec ? (
        <span className="rounded-full bg-primary/10 px-2 py-0.5 font-medium text-primary">
          ✓ 产品:按外观锚句保持
        </span>
      ) : (
        <span className="rounded-full bg-warning/10 px-2 py-0.5 text-warning">
          产品:由 AI 生成,可能与实物有出入
        </span>
      )}
      <span className="rounded-full bg-emerald-500/10 px-2 py-0.5 font-medium text-emerald-600 dark:text-emerald-400">
        ✓ 版式:全组同一套
      </span>
      <span className="rounded-full bg-emerald-500/10 px-2 py-0.5 font-medium text-emerald-600 dark:text-emerald-400">
        ✓ 风格:{styleSummary}
      </span>
    </div>
  );

  /** 组图台本卡 ① 区的产品入口(含选完的摘要与「去补图」提示,与页顶块同口径)。 */
  const groupProductSlot = (
    <div>
      <p className="text-[13px] font-semibold">这组图卖哪个商品?</p>
      {form.product ? (
        <div className="mb-1.5 mt-0.5 flex flex-wrap items-baseline gap-x-2 gap-y-1">
          <span className="text-[13.5px] font-semibold text-foreground">{form.product.name}</span>
          <span className="text-[12px] text-muted-foreground">
            {form.product.sellingPoints.length} 条卖点 · {form.product.specs.length} 项参数
          </span>
        </div>
      ) : (
        <p className="mb-1.5 mt-0.5 text-[12px] text-muted-foreground">
          名称 / 卖点 / 参数逐字带进出图指令;卖点可一键填进台本。也可以不选,直接手写。
        </p>
      )}
      <ProductPickerDrawer
        product={form.product}
        onPick={form.setProduct}
        disabled={form.submitting}
        compact
      />
      {form.product && (
        <label className="mt-1 flex items-center gap-1.5 text-[12px] text-muted-foreground">
          <input
            type="checkbox"
            checked={form.showProductSpecs}
            disabled={form.submitting}
            onChange={(e) => form.setShowProductSpecs(e.target.checked)}
          />
          在图上显示产品参数(款号 / 克重 / 尺寸等);不勾时参数只用于理解产品、按真实大小画
        </label>
      )}
      {form.product && !productSampleKey && (
        <p className="mt-1 text-[12px] text-muted-foreground">
          这个商品还没传实拍图,整组从零画 —— 画面里的商品可能与实物有出入。
          <a
            href={`/products?node=${encodeURIComponent(form.product.code)}&materials=1`}
            className="ml-1 font-semibold text-primary underline-offset-4 hover:underline"
          >
            去补图 ▸
          </a>
        </p>
      )}
      {/* 锚行(A2):有实拍才有开关;默认锚定 —— 选了产品还让引擎乱画产品,违背选它的意图 */}
      {form.product && productSampleKey && (
        <div className="mt-1.5 flex flex-wrap items-center gap-2 text-[12px]">
          {/* 锚图缩略:「换一张」不能盲换 —— 锚的是哪张照片要看得见(选择器改版同一条原则) */}
          {form.groupAnchorOn && groupAnchorPreview && (
            <img
              src={groupAnchorPreview}
              alt={`锚定实拍图 第 ${sampleIdx + 1} 张`}
              className="h-10 w-10 shrink-0 rounded-md border border-border/70 object-cover"
              data-testid="group-anchor-thumb"
            />
          )}
          {form.groupAnchorOn ? (
            <span className="rounded-full border border-emerald-500/40 bg-emerald-500/10 px-2 py-0.5 font-medium text-emerald-600 dark:text-emerald-400">
              🖼 整组照着实拍画(第 {sampleIdx + 1} 张)
            </span>
          ) : (
            <span className="rounded-full border border-border px-2 py-0.5 text-muted-foreground">
              不锚实拍,整组从零画
            </span>
          )}
          {form.groupAnchorOn && sampleImages.length > 1 && (
            <button
              type="button"
              disabled={form.submitting}
              onClick={() => setSampleIdx((sampleIdx + 1) % sampleImages.length)}
              className="font-medium text-primary underline-offset-2 hover:underline"
            >
              换一张({sampleIdx + 1}/{sampleImages.length})
            </button>
          )}
          <button
            type="button"
            disabled={form.submitting}
            onClick={() => form.setGroupAnchorOn(!form.groupAnchorOn)}
            className="text-muted-foreground underline-offset-2 hover:text-foreground hover:underline"
          >
            {form.groupAnchorOn ? "只要氛围,不锚实物" : "改回照着实拍画"}
          </button>
          {/* ★锚 × 画风冲突明示(2026-08-03 用户实测「皮克斯3D 不生效」):锚定=照着照片改,
              照片的写实质感会压过风格化画风 —— 冲突不该静默让一方赢,要说清楚并给出路 */}
          {form.groupAnchorOn && form.selectedStyle && (
            <span className="w-full text-2xs leading-relaxed text-warning">
              已锚实拍:画风「{form.selectedStyle}」主要作用于配色与版式,画面质感会以实拍为主。
              想让画风完全接管,点上面「只要氛围,不锚实物」(主体改由外观锚句保持)。
            </span>
          )}
        </div>
      )}
    </div>
  );

  /** 把命中的那段从正文摘掉(两个处置方向共用)。 */
  const stripFromContent = (matched: string) => {
    const next = stripMetaInstruction(metaHintText, matched);
    if (form.isAiOptimized) form.setContentDescription(next);
    else form.setPrompt(next);
  };

  /**
   * 段控出不出、写什么(2026-08-01)。
   * ★商品主图路没有上传口 —— 三条不全成立时整个不出现,不留一个点不动的「改上传的图」。
   */
  // A3(2026-08-03):套图带产品实拍时同样是「用这张产品图当底」;套图没产品维持自由段控
  const segMode: "free" | "goods" | "none" = productAnchorKey
    ? "goods"
    : isGoodsMain
      ? "none"
      : "free";

  const SEG_LABELS = {
    free: [
      { v: "T2I" as const, label: "从零写", Icon: Type },
      { v: "I2I" as const, label: "改上传的图", Icon: ImageIcon },
    ],
    goods: [
      { v: "I2I" as const, label: "用这张产品图当底", Icon: ImageIcon },
      { v: "T2I" as const, label: "不用原图,重新画", Icon: Type },
    ],
  };

  /** 大框标题右侧的两态段控 —— 文案随路走(自由创作 / 商品主图)。 */
  const modeSeg =
    segMode === "none" ? null : (
      <div
        className="inline-flex shrink-0 rounded-lg bg-muted/60 p-0.5"
        role="tablist"
        aria-label="创作方式"
      >
        {SEG_LABELS[segMode].map(({ v, label, Icon }) => {
          const on = (isI2I ? "I2I" : "T2I") === v;
          return (
            <button
              key={v}
              type="button"
              role="tab"
              aria-selected={on}
              onClick={() => !on && onModeChange(v)}
              className={cn(
                "inline-flex items-center gap-1.5 rounded-md px-3 py-1.5 text-[12.5px] transition-colors",
                on
                  ? "bg-background font-bold text-primary shadow-sm"
                  : "text-muted-foreground hover:text-foreground"
              )}
            >
              <Icon className="h-3.5 w-3.5" />
              {label}
            </button>
          );
        })}
      </div>
    );

  /**
   * 出图前确认单 —— **只在有我们判过的项、或会多花钱时才弹**(见 GenerateConfirmDialog)。
   * 自己把画幅、画质都定好的人点「生成」直接出图,不多一次点击。
   */
  const [confirmTrigger, setConfirmTrigger] = useState<ConfirmTrigger | null>(null);
  /**
   * B2-1:组图**默认分步** —— 先出封面(扣 1 张的钱),看过真图再到详情页续出剩下。
   * 6 张一把梭是 1680 积分,风格不对全废,而点生成之前用户看不到任何真图。
   */
  const [groupStepwise, setGroupStepwise] = useState(true);
  const stepwiseActive = groupModeActive && effectiveCount > 1 && groupStepwise;
  /**
   * 底栏预估 —— **走后端报价口 `dryRun: true`**,不再由前端自己算。
   *
   * ★ 改之前这里读的是 `/poster/provider-capabilities` 的 `tierCredits`。那是**第二个价源**:
   *   它由 ai-service 算,而结算按 `xb_price_sku` 算。出图线上两边今天恰好一致
   *   (都是 ×10),换成 ×1.6 的线就全对不上了 —— 这一批要消灭的就是「同一个价有多个出处」。
   * ★ 报不出价 → 不显示数字(不兜旧值、不兜 0)。
   */
  const quoteQty = useMemo(() => {
    const full = effectiveCountOf(detectOutputMode(form.groupPoints, form.ratioSet), {
      groupPoints: submittableGroupPoints(form.groupPoints),
      ratioSet: form.ratioSet,
      count: validCount,
    });
    // ★ 分步态**直接按 1 张报价**,不是拿总额去除张数:
    //   「先出封面」这一步真的只出一张、只扣一张的钱,而 ⌈总额÷张数⌉ 与 ⌈单张⌉
    //   在取整上不是同一个数。说「生成」却扣 6 张的钱是这一页最贵的一次误解,
    //   差一分也是同一类错。
    return stepwiseActive ? 1 : full;
  }, [form.groupPoints, form.ratioSet, validCount, stepwiseActive]);
  const quoteParams = useMemo(
    () =>
      validTier && form.provider && quoteQty > 0
        ? {
            skuCode: SKU.posterImage,
            variantKey: imageVariant(form.provider, validTier),
            qty: quoteQty,
          }
        : null,
    [form.provider, validTier, quoteQty]
  );
  const { credits: estimatedCredits } = useQuotePreview(quoteParams);
  /**
   * ★1005 生产验收:分步态的次要动作「一次全出 N 张」此前**直接提交、不报价、不确认** ——
   *   底栏写着「预计扣 28 积分」(封面那 1 张),点完实扣 112(整组 4 张)。
   *   次要动作要有自己的报价(整组张数,与提交侧 submitQuoteQty 的 "all" 同口径)并走确认卡。
   */
  const allQuoteParams = useMemo(
    () =>
      stepwiseActive && validTier && form.provider && effectiveCount > 0
        ? {
            skuCode: SKU.posterImage,
            variantKey: imageVariant(form.provider, validTier),
            qty: effectiveCount,
          }
        : null,
    [stepwiseActive, form.provider, validTier, effectiveCount]
  );
  const { credits: allEstimatedCredits } = useQuotePreview(allQuoteParams);

  const doGenerate = (
    stage?: "cover" | "all",
    quoteId?: string | null,
    confirmedRatio?: Ratio | null
  ) =>
    actions.handleGenerate(
      // ★mask 在这一刻才导出 + 上传:整段在提交的忙碌态里,不存在竞态窗口。
      //   失败就**中止提交并说清楚**,不静默退化成整图重绘。
      form.maskPainted ? () => painterRef.current!.exportNow() : undefined,
      stage ?? (stepwiseActive ? "cover" : undefined),
      quoteId,
      confirmedRatio
    );
  const requestGenerate = () => {
    const batch = groupModeActive || !!form.ratioSet;
    // 组图/套图的画幅语义不同(轮播固定比例 / 多比例各出一张),不在这一批的射程内;
    // i2i 是「改这张图」,画幅跟随原图 —— 两者都不弹画幅确认。
    const ratioRelevant = !isI2I && !batch;
    const smartRatio = ratioRelevant && !form.ratioUserTouched && !ratioDetection;
    // 0831:只有**显式数字比例**与手选打架才值得问(ratioRuling.conflictingExplicit);
    // 词面(「正方形」这类)是猜的 —— 猜的不配质疑亲手点的,不弹卡、手选直接赢。
    // 此前词面也触发弹卡,且用户答完提交侧还会拿文案再盖一遍(爱民 3:4→1:1 那单)。
    const conflictRatio = ratioRelevant ? ratioRuling.conflictingExplicit : null;
    const tier4k = (validTier ?? "") === "4K";
    if (!smartRatio && !conflictRatio && !tier4k) {
      void doGenerate();
      return;
    }
    setConfirmTrigger({
      smartRatio,
      conflictRatio,
      tier4k,
      // 1006:卡上显示/确认回传的是**裁决后的画幅**(与底栏、【画幅构图】同源),不是表单默认值 ——
      //   否则文案写了 9:16、底栏也显示 9:16,确认卡却把默认 1:1 当「用户确认」回传,一次全出 4 张全是方图。
      currentRatio: ratioRuling.ratio ?? validRatio,
      userText: [form.originalPromptSnapshot ?? form.prompt, form.extraConstraint]
        .filter((s) => s && s.trim())
        .join("\n"),
      credits: estimatedCredits ?? null,
      // 确认卡自己去落正式单;这里只把「问哪一张单」告诉它,不把预估值当成结果
      quoteParams,
      tierLabel: validTier ?? "2K",
      countLabel: `${effectiveCount} 张`,
      styleLabel: styleLabelForTone(form.selectedStyle) ?? "自由发挥",
    });
  };

  /** 整组一次出完 —— 与封面那一步不同,它一次扣整组的钱,**必须**过确认卡(见 allQuoteParams)。 */
  const requestGenerateAll = () => {
    setConfirmTrigger({
      smartRatio: false,
      conflictRatio: null,
      tier4k: (validTier ?? "") === "4K",
      wholeGroup: { count: effectiveCount },
      // 1006:卡上显示/确认回传的是**裁决后的画幅**(与底栏、【画幅构图】同源),不是表单默认值 ——
      //   否则文案写了 9:16、底栏也显示 9:16,确认卡却把默认 1:1 当「用户确认」回传,一次全出 4 张全是方图。
      currentRatio: ratioRuling.ratio ?? validRatio,
      userText: "",
      credits: allEstimatedCredits ?? null,
      quoteParams: allQuoteParams,
      tierLabel: validTier ?? "2K",
      countLabel: `${effectiveCount} 张`,
      styleLabel: styleLabelForTone(form.selectedStyle) ?? "自由发挥",
    });
  };

  return (
    <>
      {/* ── 统一来源条 + 步骤条(V1 §3.4/§3.5)────────────────────────────────
          改之前这里是两条各写各的:`SceneOriginBar`(我从哪张卡来)在这一行、
          `PosterBlueprintBar`(蓝图 + 爆点编辑)在下面一百多行处。现在收成一个挂载点、
          一套判据 —— 但**没有把信息量压平**:组图/蓝图那条仍然原样嵌爆点编辑器。 */}
      {/* ★`free` 态不渲染:它那条 79px 全宽 strip 的三句话有两句在别处逐字重复,
          唯一有增量的「挑个目的」已提到下面的次要动作条里(见那一段注释)。
          有真来源(场景卡 / 蓝图 / 模板 / 产品)时照常渲染 —— 那时它才真的在回答"我从哪来"。 */}
      {shouldRenderSourceBar(sourceBarData.kind) && (
        <PosterSourceBar data={sourceBarData} disabled={form.submitting} className="mb-2" />
      )}

      {/* 2026-07-30 —— 这里**不再**加形态条。
          ★上面那条 `PosterSourceBar` 已经在回答同一个问题(从哪张卡来 / 是哪一种产出),
            我一度在它下面又摆了一条「你正在出 商品主图」—— 同一句话说两遍,
            用户第一眼看到的就是"重复显示"。「我在哪一态」有一个出口就够了。
          ★换形态那件事(handoff)有独立价值,搬进大框贴着文案(见 PosterModeHandoff)。 */}

      {/* ── R2:左操作 + 右回显(与长图双态同构;右列只回显设置,不预演画面)──
          三档同参:≥1400 左 1fr + 右 400 / 1040-1400 左 1fr + 右 340 / <1040 上下塌陷。
          ★所有列 minmax(0,…):固定列+固定列在窄容器里必横向溢出(V2 那条老根)。 */}
      <div
        className="grid gap-5 grid-cols-[minmax(0,1fr)] min-[1040px]:grid-cols-[minmax(0,1fr)_340px] min-[1400px]:grid-cols-[minmax(0,1fr)_400px]"
        data-create-layout={isI2I ? "i2i" : "t2i"}
      >
        <div className="flex flex-col gap-4">
          {/* 顶部工具条 —— 2026-07-28 方案 B:段控删除。
              「文字 / 改图」曾经是这里的两个 tab,但它跟「爆点组图 / 多比例套图」平级
              放着并不对:那些是**产出形态**,改图是**「底图从哪来」**,不是一个维度。
              后果是改图态下半个页面的控件对它无意义(产品库/画风/配色/字体)。
              现在两条路各是卡墙兜底区的一张卡,进来就只有该模式的控件;
              这里只留一个互跳链接,免得进错了没有回头路。 */}
          {/* ── 页面级次要动作条(2026-07-31 版面收敛)────────────────────────
              ★改之前这里是**两行**:上面一条 79px 全宽的「自由创作 / 自己描述,不套目的 /
                一句话说要什么就行…」来源条,下面这一行 36px 里用一个空 `<span/>` +
                justify-between 把 105px 的「保存草稿」推到右边 —— **1058px 的行只用了 9.9%**。
              ★那条来源条在 `free` 态说的三句话里,有两句在别处逐字重复:
                页面副标题已写「用文字描述,或上传一张图来改」、大框标题下已写「写一段话就行」。
                唯一有增量的是「挑个目的」这个动作 —— 所以只把**动作**留下,壳去掉
                (有真来源时 `PosterSourceBar` 照常渲染,那时它才真的在回答"我从哪来")。
              两行 115px → 一行 36px。 */}
          <div className="flex flex-wrap items-center justify-end gap-2">
            {sourceBarData.kind === "free" && sourceBarData.onPickPurpose && (
              <Button
                type="button"
                variant="ghost"
                size="sm"
                className="h-8 gap-1.5 text-primary"
                disabled={form.submitting}
                onClick={sourceBarData.onPickPurpose}
              >
                <Sparkles className="h-3.5 w-3.5" />
                挑个目的
              </Button>
            )}
            <Button
              variant="outline"
              size="sm"
              onClick={actions.saveDraft}
              disabled={actions.saveDraftPending || form.prompt.trim().length === 0}
              className="gap-1.5"
            >
              {actions.saveDraftPending ? (
                <Loader2 className="h-3.5 w-3.5 animate-spin" />
              ) : (
                <Save className="h-3.5 w-3.5" />
              )}
              {form.currentDraftId ? t("freeCreate.updateDraft") : t("freeCreate.saveDraft")}
            </Button>
          </div>

          {/* 2026-08-01 —— 商品主图进页第一步就是选商品。
              ★位置在两个模式分支**之上**,不在 T2I 分支里面:选中带实拍图的商品会把
                模式翻到 I2I(产品图当底),放在 T2I 分支里的话它会在选完的那一刻消失 ——
                用户既换不了商品也清不掉,而那正是这条路唯一的主线控件。
              ★它排在画风带之前:这条路的主线是「先说清楚是哪个商品」,画风是修饰。
              ★允许跳过是拍过板的:页面以产品为主线,但不设闸。 */}
          {/* 组图的产品入口在台本卡 ① 区(B1),页顶块只给商品主图/套图 */}
          {productUsage && !groupModeActive && (
            /* ★2026-08-01 加重:原来是 13px 标题 + 小号 outline 触发器 + 灰色说明,
               而紧挨着的画风带是一整排 110px 大图 —— **排第一但看起来最轻**,
               用户实测反馈「有点不明显」。位置本来就对(它是这条路的主线),
               错的是权重。这与 2026-07-30 那次「段标题是容器的天然更重 → 主体反而最轻」
               同一个病:位置 ≠ 分量。 */
            <div
              data-testid="goods-main-product-picker"
              className={cn(
                "rounded-xl border p-4 transition-colors",
                form.product
                  ? "border-primary/40 bg-primary/5"
                  : "border-primary/30 bg-primary/[0.04]"
              )}
            >
              <div className="mb-1 flex items-center gap-2">
                <span className="flex h-5 w-5 items-center justify-center rounded-full bg-primary text-2xs font-bold text-primary-foreground">
                  1
                </span>
                <span className="text-[15px] font-semibold">{productBlockTitle}</span>
              </div>
              <p className="mb-3 text-[12.5px] text-muted-foreground">
                名称 / 卖点 / 参数逐字带进出图指令
                {isGoodsMain && ";有实拍图的还会拿它当底图。"}
                {groupModeActive && ";卖点可一键填进台本,有实拍图的整组照着实拍画。"}
                {isRatioUsage && ";有实拍图的直接拿实拍出三个比例。"}
              </p>
              {form.product ? (
                /* 选完把带进来的东西摆出来 —— 折叠可以,失忆不行 */
                <div className="mb-2 flex flex-wrap items-baseline gap-x-2 gap-y-1">
                  <span className="text-[15px] font-semibold text-foreground">
                    {form.product.name}
                  </span>
                  <span className="text-[12.5px] text-muted-foreground">
                    {form.product.sellingPoints.length} 条卖点 · {form.product.specs.length} 项参数
                    {form.product.summary ? " · 含产品说明" : ""}
                  </span>
                </div>
              ) : null}
              <ProductPickerDrawer
                product={form.product}
                onPick={form.setProduct}
                disabled={form.submitting}
                compact
              />
              {form.product && (
                <label className="mt-1 flex items-center gap-1.5 text-[12px] text-muted-foreground">
                  <input
                    type="checkbox"
                    checked={form.showProductSpecs}
                    disabled={form.submitting}
                    onChange={(e) => form.setShowProductSpecs(e.target.checked)}
                  />
                  在图上显示产品参数(款号 / 克重 / 尺寸等);不勾时参数只用于理解产品、按真实大小画
                </label>
              )}
              {form.product && !productSampleKey ? (
                <p className="mt-1.5 text-[12.5px] text-muted-foreground">
                  这个商品<b className="text-foreground">还没传实拍图</b>,这次从零画 ——
                  画面里的商品由 AI 生成,可能与实物有出入。
                  {/* ★深链到**这个商品的物料面板**,不是 /products 首屏:那一屏对租户写着
                      「只读」,而真正的上传口是产品行上一个悬停才显形的纯图标,还得先展开分类。
                      落首屏等于把人送到门口就不管了 —— 实测租户 42 个产品 0 张实拍图。 */}
                  <a
                    href={`/products?node=${encodeURIComponent(form.product.code)}&materials=1`}
                    className="ml-1 font-semibold text-primary underline-offset-4 hover:underline"
                  >
                    去补图 ▸
                  </a>
                </p>
              ) : (
                <p className="mt-1.5 text-[12.5px] text-muted-foreground">
                  也可以不选,直接往下写 —— 那就跟「我自己描述」一样。
                </p>
              )}
              {/* A3 锚行:实拍已接成底图时说清楚锚的是哪张,多张给「换一张」(默认第 1 张主图,不自动挑) */}
              {form.product && productAnchorKey && (
                <div className="mt-1.5 flex flex-wrap items-center gap-2 text-[12px]">
                  <span className="rounded-full border border-emerald-500/40 bg-emerald-500/10 px-2 py-0.5 font-medium text-emerald-600 dark:text-emerald-400">
                    🖼 {isRatioUsage ? "本套锚定" : "底图"}:「{form.product.name}」的实拍图(第{" "}
                    {sampleIdx + 1} 张)
                  </span>
                  {sampleImages.length > 1 && (
                    <button
                      type="button"
                      disabled={form.submitting}
                      onClick={() => setSampleIdx((sampleIdx + 1) % sampleImages.length)}
                      className="font-medium text-primary underline-offset-2 hover:underline"
                    >
                      换一张({sampleIdx + 1}/{sampleImages.length})
                    </button>
                  )}
                </div>
              )}
            </div>
          )}

          {isI2I ? (
            /* ───── 模式 B:图改图 ───── */
            <>
              <RefImageUpload
                previewUrl={form.refImage?.previewUrl ?? null}
                uploading={uploadRefMut.isPending}
                onPick={(file) => uploadRefMut.mutate(file)}
                onClear={clearRefImage}
                // 下面就是画笔(它已经把原图放大显示了)→ 这里收成一行,别让同一张图占两屏;
                // 没有画笔(非精绘档)时原图只在这里出现,照常大图显示
                compact={!!form.refImage?.previewUrl && maskSupported}
                // 商品主图路:底图来自产品库,不是用户传的(文案换口径 + 清空归段控)
                productSourced={!!productAnchorKey}
              />
              {multiRefCard}
              {/* R5:圈哪改哪 —— 涂出要改的区域(不涂 = 整图重绘,与改造前行为一致)
                  B5(0925):OPENAI 家族局部重绘没接。
                  ★1020:原来照样摆出整块画布再整体置灰 —— 默认档就是 OPENAI,于是改图页
                    默认就有一大块点不动的控件。现在只在精绘档出画布,其余档一行话指路。 */}
              {form.refImage?.previewUrl &&
                (maskSupported ? (
                  <MaskPainter
                    ref={painterRef}
                    imageUrl={form.refImage.previewUrl}
                    disabled={form.submitting}
                    onPaintedChange={form.setMaskPainted}
                  />
                ) : (
                  <p className="-mt-1 px-1 text-[12.5px] text-muted-foreground">
                    只想改图里的一块?在 ⚙ 参数里把出图档位换成「精绘」,就能圈出要改的区域。
                  </p>
                ))}
              {/* R3 改图工坊:能力卡给指令打底(卡不是表单,指令框才是唯一真相) */}
              <RemixRecipePicker
                activeKey={remixRecipeKey}
                onPick={applyRecipe}
                disabled={form.submitting}
                describing={describing}
              />
              <PromptPanel
                modeSlot={modeSeg}
                prompt={form.prompt}
                setPrompt={form.setPrompt}
                polishing={form.polishing}
                onPolish={actions.handleAiOptimize}
                failureMessage={form.optimizeFailureMessage}
                paramsSlot={paramsSlot}
                hidePolish
                /* 2026-07-30 —— i2i 也要有那一行入口:「特别要求」原来在框外那个
                   `<details>` 里,两条路共用;那个块删掉之后,不给 i2i 挂上就等于
                   **改图这条路的硬性要求整个消失** —— 半个搬迁比不搬更糟。
                   产品/叠加物对改图没有意义(那是改这张图,不是造一张新的),所以只给约束项。 */
                entriesSlot={
                  <PromptEntries
                    extraOnly
                    extraConstraint={form.extraConstraint}
                    setExtraConstraint={form.setExtraConstraint}
                    disabled={form.submitting}
                  />
                }
                title={t("freeCreate.i2iInstructionTitle")}
                hint={t("freeCreate.i2iInstructionHint")}
                placeholder={t("freeCreate.i2iInstructionPlaceholder")}
              />
              {/* i2i 也可叠品牌 logo/QR(后合成 overlay) */}
              <BrandPanel
                brand={brand}
                logoId={form.logoId}
                setLogoId={form.setLogoId}
                qrId={form.qrId}
                setQrId={form.setQrId}
                useBrandColors={form.useBrandColors}
                setUseBrandColors={form.setUseBrandColors}
                printContact={form.printContact}
                setPrintContact={form.setPrintContact}
                logoAnchor={form.logoAnchor}
                setLogoAnchor={form.setLogoAnchor}
                qrAnchor={form.qrAnchor}
                setQrAnchor={form.setQrAnchor}
              />
            </>
          ) : (
            /* ───── 模式 A:文字生成(默认)─────
               2026-07-29 T1 再排:画成什么样 → 说什么 → 叠什么。
               ★2026-07-28 那版是「说什么 → 画成什么样 → 叠什么」,后两段折叠 ——
                 用户写完描述直接点生成,②③ 一次都没打开过(用户原话:「很容易忘记选择」)。
               ★当初把样式段放后面的理由(见 CreateSection 文件头)是「两段文字输入被五块
                 选择器隔开 900px」。那个理由在三段重排之后**已经不成立**:爆点和创作描述
                 现在同在「说什么」一段里相邻,把样式提到前面隔开的是「选择器 ↔ 文字」,
                 不再隔开「文字 ↔ 文字」。
               ★「叠什么」不跟着提前,两条理由指向同一个位置:① LOGO/二维码走的是出图后
                 overlay 合成、物理上就是最后一层,段序=流程序;② 它有真正合理的默认值
                 (不叠)。画风没有 ——「自由发挥」是放弃选择、不是默认值,这正是它必须被
                 看见的原因。 */
            <>
              {/* 2026-07-30 —— 拆掉 ①② 两层 `CreateSection` 包装。
                  ★原来是「卡里套卡、标题上还有标题」:段标题(① 画成什么样)是容器的,
                    框标题(想要一张什么样的图?)是内容的 —— 容器的标题天然更重,
                    于是这一页最重要的那个框反而看起来最轻。用户原话:
                    「想要一张什么样的图明显就锁进的比 2 说什么厉害」。
                  ★现在只有一层:画风带是无边框辅助层,大框是唯一的重元素。 */}
              {/* C(2026-08-03):组图态三条风格带不平铺 —— 组图全组必须一个风格,
                  风格是台本的属性,收进台本卡「整组风格」chip(弹层里还是这个 StylePanel)。 */}
              {!groupModeActive && (
                <div id="create-style-section">
                  <StylePanel
                    selectedStyle={form.selectedStyle}
                    setSelectedStyle={actions.handleStyleChange}
                    fontStyle={form.fontStyle}
                    setFontStyle={form.setFontStyle}
                    styleTokens={form.styleTokens}
                    setStyleTokens={form.setStyleTokens}
                    followStyle={form.followStyle}
                    resetFollowStyle={form.resetFollowStyle}
                  />
                </div>
              )}

              <div>
                {/* 2026-07-30 —— 产品选择器搬进大框那一行(见 PromptEntries):
                    它回答的是「这张图还要带什么」,与那段文案是同一件事。
                    这里不留第二入口 —— 同一件事两个入口迟早漂成两套状态。 */}
                {/* B1(2026-08-03):组图态 = 一张 hero 台本卡,总(产品+整组说明)→ 分(逐张)。
                    原 hero 大框(PromptPanel)在组图态**不渲染** —— 它的真实职责(整组共同
                    说明)以 brief 的身份进了台本卡 ① 区,仍绑 form.prompt,提交契约零改。
                    ⚙参数 与 品牌入口条 两个槽随之搬进卡底;AI 润色/超载建议/排版提示是
                    单图语境的控件,组图态不带。 */}
                {groupModeActive ? (
                  <GroupScriptPanel
                    points={form.groupPoints ?? []}
                    onPointsChange={form.setGroupPoints}
                    disabled={form.submitting}
                    fillFrom={
                      /* ★AI 排过台本后逐字填充入口退场(2026-08-03 用户实测):AI 排的
                         大字本来就改写自这些卖点(ⓘ 可核),再给「把卖点填进台本」只会
                         追加一份更差的原文重复。判据 = 当前台本里还有 AI 排的卡
                         (title 在 draftSources 里);删光 AI 卡入口自动回来。 */
                      form.product &&
                      form.product.sellingPoints.length &&
                      !(form.groupPoints ?? []).some((pt) => draftSources[pt.title.trim()])
                        ? {
                            productName: form.product.name,
                            sellingPoints: form.product.sellingPoints,
                          }
                        : null
                    }
                    brief={form.prompt}
                    onBriefChange={form.setPrompt}
                    productSlot={groupProductSlot}
                    styleSlot={groupStyleSlot}
                    draftSlot={groupDraftSlot}
                    consistencySlot={groupConsistencySlot}
                    sourceOf={(t) => draftSources[t]}
                    paramsSlot={paramsSlot}
                    entriesSlot={promptEntriesNode}
                  />
                ) : null}
                {/* 2026-07-30 —— 单图态下那个「组图模式 · 按爆点分张」按钮删掉。
                    ★它是**页内形态切换**的残留:我上一批把 ⚙ 弹层里的「出什么」三选一删了
                      (理由:形态是能力切换不是参数,页内切换会让内容整块换掉),却漏了这一个,
                      同一个病还留在页面上 —— 用户原话「爆点我们就走爆点的卡片」。
                    ★组图的入口是**卡墙那张「爆点组图」卡**与**追爆舱的拆解报告 handoff**
                      (`parsePosterExplosiveState` → `deriveGroupPoints`,爆点主要来源是报告
                      而不是用户手打)。页内不再有"切到组图"这条路。
                    ★爆点编辑器本身只在组图态渲染,单图态从来看不到它 —— 这一点没变。 */}

                {/* 2026-07-30 —— **同一个框**。
                    改之前这里是 `form.isAiOptimized ? <OptimizedContentSection/> : <PromptPanel/>`
                    两个组件互斥渲染:点一下整理,框换了、按钮换了、卡片标题也换了,
                    用户以为进了另一个页面。现在产物落回原来那个框,他接着改就是了。
                    B1(2026-08-03):组图态整卡不渲染 —— 见上方台本卡注释。 */}
                {!groupModeActive && (
                  <PromptPanel
                    modeSlot={modeSeg}
                    /* 稿子里这个框的标题就是一句问话 + 一句「写一段话就行」——
                     它是页面主体,标题该说「你要干什么」而不是「这个控件叫什么」。 */
                    /* A3(2026-08-03):套图态「一张」改「一套」—— 卡面承诺三比例,标题还问
                     "一张"就是名不副实(与「宽图任务改口叫长图」同款病)。 */
                    title={form.ratioSet ? "想要一套什么样的图?" : "想要一张什么样的图?"}
                    hint={
                      form.ratioSet
                        ? "一段话说内容 —— 每个比例出同一个内容,构图各自适配"
                        : "写一段话就行"
                    }
                    prompt={form.prompt}
                    setPrompt={form.setPrompt}
                    polishing={form.polishing}
                    onPolish={actions.handleAiOptimize}
                    failureMessage={form.optimizeFailureMessage}
                    paramsSlot={paramsSlot}
                    isAiOptimized={form.isAiOptimized}
                    contentDescription={form.contentDescription}
                    setContentDescription={form.setContentDescription}
                    systemConstraints={form.systemConstraints}
                    coverageChips={form.coverageChips}
                    onRestoreOriginal={actions.handleRestoreOriginal}
                    onReOptimize={actions.handleReOptimize}
                    /* 2026-08-01 —— 产品入口从这里迁走。
                     ★判据变了:不再是「因为你进了商品主图这个入口」,而是
                       「因为你选的用途需要产品资料」。不带用途的自由创作页面上
                       不该有产品树 —— 用户原话「文生图图生图需要简单一些」。
                     ★`ProductPickerDrawer` 没删:商品主图那条路要用(批次 2)。
                     ★去处由 PromptEntries 自己说(没有 productSlot 时出「去商品主图 ▸」),
                       不在这里再写一遍 —— 同一句话两个出口迟早漂。 */
                    finalLen={finalPromptLen}
                    finalMax={RAW_PROMPT_MAX}
                    entriesSlot={promptEntriesNode}
                    metaHintSlot={
                      metaHint ? (
                        // 任务 19 —— 问一句,不替他改。
                        // ★措辞先说**我们看到了什么**(逐字回显他写的那几个字),再说去处。
                        //   不写"你写错了":同一句话放对地方就是参数,放错才是正文,不是他的错。
                        <p
                          className="text-[12px] leading-relaxed text-muted-foreground"
                          data-meta-hint={metaHint.kind}
                        >
                          <span className="text-foreground">「{metaHint.matched}」</span>
                          {" 看起来是"}
                          <b className="font-semibold text-foreground">排版要求</b>
                          {",不是要印在图上的字 —— 写在这里会被当成正文画进去。"}
                          {metaHint.kind === "font" ? (
                            <button
                              type="button"
                              data-meta-hint-apply
                              onClick={() => {
                                form.setFontStyle(metaHint.fontKey!);
                                stripFromContent(metaHint.matched);
                              }}
                              className="mx-1 font-semibold text-primary underline-offset-4 hover:underline"
                            >
                              设成字体「{metaHint.fontLabel}」▸
                            </button>
                          ) : (
                            <button
                              type="button"
                              data-meta-hint-apply
                              onClick={() => {
                                // 挪进「特别要求」:那里的话进提示词当**约束**,不当正文。
                                const prev = form.extraConstraint.trim();
                                form.setExtraConstraint(
                                  prev ? `${prev};${metaHint.matched}` : metaHint.matched
                                );
                                stripFromContent(metaHint.matched);
                              }}
                              className="mx-1 font-semibold text-primary underline-offset-4 hover:underline"
                            >
                              挪进「特别要求」▸
                            </button>
                          )}
                          <button
                            type="button"
                            data-meta-hint-dismiss
                            onClick={() => setMetaHintDismissed(metaHint.matched)}
                            className="text-muted-foreground underline-offset-4 hover:underline"
                          >
                            照原样印上去
                          </button>
                        </p>
                      ) : undefined
                    }
                    overloadSlot={
                      overload ? (
                        // ★依据是**版面容量**,不是"每张讲一个",也不是"删掉几条卖点":
                        //   一张排得下约 6 条 → 20 条 ≈ 4 张。卖点是用户的,我们无权替他删;
                        //   而张数由排布推出来,这条建议才站得住(见 overloadHint 头注)。
                        <p className="text-[12px] leading-relaxed text-muted-foreground">
                          <span className="text-foreground">
                            识别到 {overload.count} 条
                            {overload.from === "product" ? "产品卖点" : "分点内容"}
                          </span>
                          {" —— "}一张海报排得下约 {POINTS_PER_IMAGE} 条,再多容易挤成小字。
                          <button
                            type="button"
                            onClick={() =>
                              navigate("/ai-haibao/create", {
                                state: {
                                  scenePreset: { kind: "poster_group", groupPoints: [] },
                                  prefillPrompt: form.prompt,
                                },
                              })
                            }
                            className="mx-1 font-semibold text-primary underline-offset-4 hover:underline"
                          >
                            分成 {overload.suggest} 张出 ▸
                          </button>
                          每张排 {POINTS_PER_IMAGE} 条左右,都放得下。
                        </p>
                      ) : undefined
                    }
                    handoffSlot={
                      !isI2I && form.prompt.trim() ? (
                        <PosterModeHandoff
                          mode={form.ratioSet ? "ratioSet" : "single"}
                          prompt={form.prompt}
                          disabled={form.submitting}
                        />
                      ) : undefined
                    }
                  />
                )}

                {/* 2026-07-30 —— 指令预览搬到右回显列(见 CreateMirrorColumn 头注)。
                    它原本紧贴文案框下方,而右列同时在复述画风/配色/费用:
                    左边越来越长、右边没什么意义。现在左边只管「写与选」,
                    右边一列专门回答「这些到底变成了什么话」。 */}
              </div>

              {/* 2026-07-30 —— 段③「叠什么」整段搬进大框的 chip(见 PromptEntries)。
                  它有真正合理的默认值(不叠),但摘要必须在 chip 上看得见。 */}
            </>
          )}

          {/* W2-B9 多图参考(模特试戴):文字模式在这里;图改图模式挪到「要改的图」正下方(见 multiRefCard)。 */}
          {!isI2I && multiRefCard}

          {/* 2026-07-30 —— 「特别要求」已经收进大框那一行(见 PromptEntries)。
              ★这里原来还留着一份 `<details>`,于是框里一个、框外一个 —— 用户第一眼就问
                「框里有个特别要求了怎么底下还有一个」。同一件事两个入口:两处各自读写同一个
                `form.extraConstraint`,看起来"同步"了,但用户根本不知道该信哪个;
                而我上一批亲口写的纪律就是"不留第二入口"。删。
              ★i2i 那条路也走同一个大框(PromptPanel),所以它的特别要求一并在框里。 */}

          {/* 最短长度提示(audit-0713 D3):旧实现 prompt 太短只静默禁用生成按钮,用户不知为何点不动。
          非空但未达下限时给中文可读提示。i2i 下限 2 字,t2i 5 字(与 canSubmit 同源)。 */}
          {remixSlotUnfilled && (
            <p className="text-[13px] text-amber-600 dark:text-amber-500">
              指令里还留着〈…〉待填段,把它换成你的要求再出图(卡片只是给指令打底)。
            </p>
          )}
          {form.prompt.trim().length > 0 && form.prompt.trim().length < (isI2I ? 2 : 5) && (
            <p className="text-[13px] text-amber-600 dark:text-amber-500">
              {t("freeCreate.minPromptHint", { n: isI2I ? 2 : 5 })}
            </p>
          )}
        </div>

        <CreateMirrorColumn
          /* P4:多张时把每张的画幅画出来(组图配爆点标题、套图各按自己的比例) */
          previewCells={
            groupModeActive
              ? (collectedPointsForCount ?? []).map((p, i) => ({
                  ratio: validRatio,
                  caption: p.title,
                  // B1-4:封面标只打在第 1 张,且用户没把这一档关掉时才打
                  badge: isCoverAt(collectedPointsForCount ?? [], i) ? "封面" : undefined,
                }))
              : form.ratioSet && form.ratioSet.length >= 2
                ? form.ratioSet.map((r) => ({ ratio: r }))
                : []
          }
          ratioLabel={effectiveRatioLabel}
          ratio={mirrorRatio}
          // codex P2:组图/套图的真实出图数来自 groupPoints/ratioSet(费用估算就是这么算的),
          // 传 validCount 会出现「1 张」配「三张的钱」
          count={effectiveCount}
          batchLabel={groupModeActive ? "爆点组图" : form.ratioSet ? "全渠道套图" : null}
          consistency={consistency}
          styleLabel={styleLabelForTone(form.selectedStyle) ?? "自由发挥 · AI 据内容判断"}
          styleThumbUrl={mirrorStyleThumb}
          styleTokens={form.styleTokens}
          followStyle={form.followStyle}
          fontLabel={mirrorFontLabel}
          logoOn={!!form.logoId}
          qrOn={!!form.qrId}
          brandColorsOn={form.useBrandColors}
          providerLabel={providerLabel}
          tierLabel={validTier ?? "2K"}
          /* ★报价不在这一列 —— 合 main 时按对方那条更新的决定走(同一笔钱不说两遍,
             留底栏那个「预计扣 N 积分」,它挨着掏钱的按钮)。本批原来在这里加的
             「整组 N 张 · 本次先出封面 X」也随之撤掉:那句话是为了消解**两个数字同屏**
             的歧义,而歧义的根源已经被对方连根去掉了。分步各步的价钱仍逐步明示
             (底栏「先出封面」报本次、详情页续出条报剩下几张)。 */
          /* 本列主体:左边写什么选什么,这里当场显示会发给引擎的那几段(默认展开)。 */
          promptPreview={
            <FinalPromptFold
              prompt={form.prompt}
              isAiOptimized={form.isAiOptimized}
              absorbed={form.absorbed}
              contentDescription={form.contentDescription}
              systemConstraints={form.systemConstraints}
              product={form.product}
              showProductSpecs={form.showProductSpecs}
              selectedStyle={form.selectedStyle}
              styleTokens={form.styleTokens}
              fontStyle={form.fontStyle}
              ratio={compositionRatio}
              onInline={inlineSegment}
              /* 组图这一列有 6 段指令,默认展开会把「会出几张 / 像不像一套」挤出屏幕 —— 折叠 */
              defaultOpen={!groupModeActive}
            />
          }
        />
      </div>

      <PosterSubmitFooter
        count={effectiveCount}
        batchLabel={
          stepwiseActive
            ? `先出封面这 1 张,满意了再照着它出剩下 ${effectiveCount - 1} 张`
            : groupModeActive
              ? `${effectiveCount} 个爆点各出一张`
              : form.ratioSet && form.ratioSet.length >= 2
                ? `${effectiveCount} 个比例各出一张`
                : undefined
        }
        canGenerate={canSubmit}
        disabledReason={submitDisabledReason}
        priceUnavailable={capabilitiesDown}
        busy={form.submitting || form.polishing}
        busyLabel={form.submitting ? t("freeCreate.submitting") : t("freeCreate.polishing")}
        /* B2-1:分步态主按钮只承诺封面那一张,费用也只报那一张的 ——
           说「生成」却扣 6 张的钱,是这一页最贵的一次误解。 */
        generateLabel={stepwiseActive ? "先出封面" : t("freeCreate.generate")}
        /* 分步态的单张价已经在 quoteQty 里按 1 张问过了 —— 这里不再做除法 */
        estimatedCredits={estimatedCredits ?? undefined}
        secondaryAction={
          stepwiseActive
            ? {
                label:
                  allEstimatedCredits != null && !capabilitiesDown
                    ? `一次全出 ${effectiveCount} 张 · ${allEstimatedCredits} 积分`
                    : `一次全出 ${effectiveCount} 张`,
                onClick: requestGenerateAll,
              }
            : null
        }
        ratioLabel={effectiveRatioLabel}
        styleLabel={isI2I ? undefined : `${styleSummary} · ${brandSummary}`}
        onJumpToStyle={() =>
          document
            .getElementById("create-style-section")
            ?.scrollIntoView({ behavior: "smooth", block: "start" })
        }
        onGenerate={requestGenerate}
        scheduleMode={form.scheduleMode}
        setScheduleMode={form.setScheduleMode}
        scheduledAt={form.scheduledAt}
        setScheduledAt={form.setScheduledAt}
      />

      {/* 确认单:用户在这里认可/改过的画幅**当成手选**(它确实是他的选择),
          于是提交侧 `ratioForReq` 与【画幅构图】段都跟着它走,不会再判一次。 */}
      <GenerateConfirmDialog
        trigger={confirmTrigger}
        onCancel={() => setConfirmTrigger(null)}
        onConfirm={(ratio, quoteId) => {
          setConfirmTrigger(null);
          if (ratio) {
            // 回填 UI 态(下次生成不再弹卡),但**这一单不靠它**:state 同一拍还没落,
            // handleGenerate 的 closure 读到的仍是旧值 → 智能路会再抽一次 5 选 1,
            // 卡上说 9:16 发出去 3:4(0909 月饼单实锤)。终值必须显式随调用传下去。
            form.setRatio(ratio);
            form.setRatioUserTouched(true);
          }
          // ★ 单号原样往下传 —— 确认卡上显示的那个数就是这张单的数,扣的也得是它
          // 整组一次出完那张卡:张数/单都按整组,stage 必须显式 "all"(单是按整组张数落的)
          if (confirmTrigger?.wholeGroup) {
            setGroupStepwise(false);
            void doGenerate("all", quoteId, ratio);
            return;
          }
          void doGenerate(undefined, quoteId, ratio);
        }}
      />
    </>
  );
}
